import { showUserAlert, userErrorMessage } from "../utils/userErrorMessage";
import { createReservationBatch, hasExistingCopyRoute } from "../services/reservationWrites";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import TimeSelect from "../components/ui/time-select";
import { supabase, supabaseKey, supabaseUrl } from "../supabase/client";
import { useNavigate } from "react-router-dom";
import usePersistedUI from "../hooks/usePersistedUI";
import { clampDateString, normalizeDateRange, validateNativeDateInputs } from "../utils/dateUtils";
import {
  getCarConflictInfo,
  getCarNumberMatchKey,
  getRowKey,
  hasReservationTimeConflict,
} from "../utils/dispatchConflictUtils";
import {
  clearDriverAssignment,
  DRIVER_HOLIDAY_BLOCK_MESSAGE,
  findDriverHolidayConflict,
} from "../utils/driverHolidayUtils";

const SMS_SENDING_PAUSED = true;
const KOREAN_COLLATOR = new Intl.Collator("ko", { sensitivity: "base", numeric: true });
const DISPATCH_FETCH_LIMIT = 2000;
const COPY_BATCH_LIMIT = 20000;
const DISPATCH_FILTER_KEYS = ["사용자", "출발지", "행선지", "차량", "기사"];
const MOBILE_DISPATCH_FILTER_OPTIONS = [
  { value: "사용자", label: "사용자/노선명" },
  { value: "출발지", label: "출발지" },
  { value: "행선지", label: "행선지" },
  { value: "차량", label: "차량" },
  { value: "기사", label: "기사" },
];

function parseDateOnly(value) {
  const match = String(value || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
}

function formatDateOnly(date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function formatTime(value) {
  if (!value) return "";
  return String(value).slice(0, 5);
}

function getTimeSortValue(value) {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return Number.MAX_SAFE_INTEGER;
  return Number(match[1]) * 60 + Number(match[2]);
}

function getDatesBetween(startDate, endDate) {
  const start = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);
  if (!start || !end || start > end) return [];

  const dates = [];
  const cursor = new Date(start);
  while (cursor <= end) {
    dates.push(formatDateOnly(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

function getCopyFailureMessage(error) {
  const code = String(error?.code || "");
  const status = Number(error?.status || error?.context?.status || 0);
  const message = String(error?.message || "");
  if (code === "57014" || status === 504 || /statement timeout|canceling statement due to statement timeout/i.test(message)) {
    return "복사 실패: 처리 시간이 초과되었습니다. 복사 범위를 줄여 다시 시도하거나 서버의 복사 함수를 업데이트해 주세요.";
  }
  if (code === "23505") return "복사 실패: 복사 대상과 중복되는 예약이 있습니다. 대상 날짜를 확인해 주세요.";
  if (code === "PGRST202") return "복사 실패: 서버의 예약 복사 함수를 찾지 못했습니다. DB 함수 배포 상태를 확인해 주세요.";
  return `복사 실패: ${userErrorMessage(error)}`;
}

function escapeCsvCell(value) {
  const text = String(value ?? "");
  if (/[",\r\n]/.test(text)) return `"${text.replace(/"/g, '""')}"`;
  return text;
}

function makeSafeFilename(value) {
  return String(value || "")
    .replace(/[\\/:*?"<>|]/g, "_")
    .replace(/\s+/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

  const getTomorrow = () => {
    const today = new Date();
    today.setDate(today.getDate() + 1); // 하루 추가
    return today.toISOString().split("T")[0]; // 'YYYY-MM-DD'
  };

function getInclusiveDateSpanDays(dateRange = {}) {
  const parseDate = (value) => {
    if (!value) return null;
    const [year, month, day] = String(value).split("-").map(Number);
    if (!year || !month || !day) return null;
    const parsed = new Date(year, month - 1, day);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const from = parseDate(dateRange.from);
  const to = parseDate(dateRange.to);
  if (!from || !to) return 0;

  return Math.floor((to.getTime() - from.getTime()) / 86400000) + 1;
}

function hasDispatchSearchKeyword(filters = {}) {
  return DISPATCH_FILTER_KEYS.some((key) =>
    String(filters?.[key] || "").trim()
  );
}
  

export default function Reservation({ type = "전체", reservationMode = "예약" }) {
  const [filteredData, setFilteredData] = useState([]);
  const [carSearchList, setCarSearchList] = useState([]);
  const [searchingIndex, setSearchingIndex] = useState(null);
  const [copySelectedIds, setCopySelectedIds] = useState(new Set());
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyDateMode, setCopyDateMode] = useState("single");
  const [copyTargetDate, setCopyTargetDate] = useState("");
  const [copyRangeStartDate, setCopyRangeStartDate] = useState("");
  const [copyRangeEndDate, setCopyRangeEndDate] = useState("");
  const [copyDateFilter, setCopyDateFilter] = useState("all");
  const [copying, setCopying] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmingTentative, setConfirmingTentative] = useState(false);
  const [unassignedLoading, setUnassignedLoading] = useState(false);
  const [dispatchRuleOpen, setDispatchRuleOpen] = useState(false);
  const [dispatchRuleLoading, setDispatchRuleLoading] = useState(false);
  const [dispatchRuleApplying, setDispatchRuleApplying] = useState(false);
  const [dispatchRules, setDispatchRules] = useState([]);
  const [selectedDispatchRuleIds, setSelectedDispatchRuleIds] = useState(new Set());
  const [dispatchRuleStartDriverIds, setDispatchRuleStartDriverIds] = useState({});
  const [dispatchRulePreview, setDispatchRulePreview] = useState([]);
  const [selectedDispatchPreviewIds, setSelectedDispatchPreviewIds] = useState(new Set());
  const [reservationMemoModal, setReservationMemoModal] = useState(null);
  const [reservationMemoSaving, setReservationMemoSaving] = useState(false);
  const [busInspectionKeys, setBusInspectionKeys] = useState(new Set());
  const [mobileExpandedRows, setMobileExpandedRows] = useState(new Set());

  const [saving, setSaving] = useState(false);
  const [uiLocked, setUiLocked] = useState(false);
  
  const [carIndex, setCarIndex] = useState(new Map());
  const [carListAll, setCarListAll] = useState([]); // 자동완성 원본
  const [driverIndex, setDriverIndex] = useState(new Map());

  const [originalMap, setOriginalMap] = useState(new Map());
  const carSuggestTimerRef = useRef(null);
  const fromDateInputRef = useRef(null);
  const toDateInputRef = useRef(null);

  const navigate = useNavigate();
  const isTentativeMode = reservationMode === "가예약";
  const activeReservationTable = isTentativeMode ? "tentative_reservations" : "reservations";

  const ensureNoDriverHolidayConflict = async (rows = []) => {
    try {
      const conflict = await findDriverHolidayConflict(rows, localStorage.getItem("email"));
      if (conflict) {
        showUserAlert(DRIVER_HOLIDAY_BLOCK_MESSAGE);
        return false;
      }
      return true;
    } catch (error) {
      console.error("기사 휴일 확인 실패:", error);
      showUserAlert("기사 휴일 정보를 확인하지 못했습니다.");
      return false;
    }
  };

  const hasDriverHolidayConflictForRow = async (row) => {
    try {
      const conflict = await findDriverHolidayConflict([row], localStorage.getItem("email"));
      if (conflict) {
        showUserAlert(DRIVER_HOLIDAY_BLOCK_MESSAGE);
        return true;
      }
      return false;
    } catch (error) {
      console.error("기사 휴일 확인 실패:", error);
      showUserAlert("기사 휴일 정보를 확인하지 못했습니다.");
      return true;
    }
  };

  const validateDriverHolidayCell = async (index) => {
    const row = filteredData[index];
    if (!row || !String(row.human_name || "").trim()) return;
    if (!(await hasDriverHolidayConflictForRow(row))) return;

    setFilteredData((prev) => {
      const next = [...prev];
      if (!next[index]) return prev;
      next[index] = clearDriverAssignment(next[index]);
      return next;
    });
  };


  const handlePrint = () => window.print();

  const getCsvCellValue = (row, header, index) => {
    const map = {
      "순번": index + 1,
      "구분": row.reservation_type || "",
      "운행일": row.date || "",
      "사용자/노선명": row.user || "",
      "차량번호": row.car_number || "",
      "기사명": row.human_name || "",
      "기사연락처": row.phone || "",
      "편도/왕복": row.one_round || "",
      "시간": formatTime(row.time),
      "출발시간": formatTime(row.time),
      "도착시간": formatTime(row.arrival_time),
      "회차시간": formatTime(row.round_trip_time),
      "출발지": row.start || "",
      "경유지": row.transit || "",
      "행선지": row.destination || "",
      "받을금액": row.money || "",
    };
    return map[header] ?? "";
  };

  const getCsvSearchConditionText = (applied = ui.state.applied || ui.state) => {
    const dateRange = applied?.dateRange || ui.state.dateRange || {};
    const filters = applied?.filters || ui.state.filters || {};
    const filterText = Object.entries(filters)
      .filter(([, value]) => String(value || "").trim())
      .map(([key, value]) => `${key}: ${value}`)
      .join(" / ");
    const sortText = (applied?.sortOrder || ui.state.sortOrder) === "car" ? "차량번호순" : "날짜순";

    return {
      dateText: `${dateRange.from || ""} ~ ${dateRange.to || ""}`,
      conditionText: [
        `구분: ${type}`,
        `예약구분: ${reservationMode}`,
        `정렬: ${sortText}`,
        filterText ? `검색어: ${filterText}` : "검색어: 없음",
      ].join(" / "),
    };
  };

  const handleDownloadCsv = () => {
    const { dateText, conditionText } = getCsvSearchConditionText();
    const rows = [
      ["조회 운행일", dateText],
      ["검색조건", conditionText],
      [],
      dispatchTableHeaders,
      ...filteredData.map((row, index) =>
        dispatchTableHeaders.map((header) => getCsvCellValue(row, header, index))
      ),
    ];

    const csv = rows.map((row) => row.map(escapeCsvCell).join(",")).join("\r\n");
    const blob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const applied = ui.state.applied || ui.state;
    const dateRange = applied?.dateRange || {};
    const filters = applied?.filters || {};
    const filterName = Object.entries(filters)
      .filter(([, value]) => String(value || "").trim())
      .map(([key, value]) => `${key}-${value}`)
      .join("_");
    const filename = makeSafeFilename(
      `${dateRange.from || "조회일"}~${dateRange.to || "조회일"}_${type}_${reservationMode}_${filterName || "전체"}`
    );

    const link = document.createElement("a");
    link.href = url;
    link.download = `${filename}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const openReservationMemoModal = (index, row) => {
    setReservationMemoModal({
      index,
      id: row.id,
      user: row.user || "",
      memo: row.memo || "",
    });
  };

  const closeReservationMemoModal = () => {
    if (reservationMemoSaving) return;
    setReservationMemoModal(null);
  };

  const saveReservationMemo = async () => {
    if (!reservationMemoModal || reservationMemoSaving) return;
    if (!reservationMemoModal.id) {
      showUserAlert("메모를 저장할 예약 ID가 없습니다.");
      return;
    }

    setReservationMemoSaving(true);
    const memo = reservationMemoModal.memo || null;
    const company = localStorage.getItem("email") || "";
    const { error } = await supabase
      .from(activeReservationTable)
      .update({ memo })
      .eq("id", reservationMemoModal.id)
      .eq("company", company);

    setReservationMemoSaving(false);

    if (error) {
      showUserAlert("메모 저장 실패: " + error.message);
      return;
    }

    setFilteredData((prev) =>
      prev.map((row, index) =>
        index === reservationMemoModal.index || row.id === reservationMemoModal.id
          ? { ...row, memo: reservationMemoModal.memo || "" }
          : row
      )
    );
    setReservationMemoModal(null);
  };

  // 1) ✅ 공용 훅으로 상태 통합
  const ui = usePersistedUI(
    "reservations", // 화면별 고유 키
    {
      dateRange: { from: getTomorrow(), to: getTomorrow() },
      filters: { 사용자: "", 출발지: "", 행선지: "", 차량: "", 기사: "" },
      sortOrder: "date",
      applied: null, // 마지막으로 '검색' 눌러서 적용한 스냅샷
      // 필요 시 category 등도 추가 가능
    },
    { urlSync: true } // URL 쿼리와 동기화
  );
  const [mobileFilterKey, setMobileFilterKey] = useState(() =>
    DISPATCH_FILTER_KEYS.find((key) => String(ui.state.filters?.[key] || "").trim()) || "사용자"
  );

  const handleMobileFilterKeyChange = (nextKey) => {
    setMobileFilterKey(nextKey);
    ui.setPartial({
      filters: DISPATCH_FILTER_KEYS.reduce((next, key) => ({ ...next, [key]: "" }), {}),
    });
  };

  const handleMobileFilterTextChange = (value) => {
    ui.setPartial({
      filters: DISPATCH_FILTER_KEYS.reduce(
        (next, key) => ({ ...next, [key]: key === mobileFilterKey ? value : "" }),
        {}
      ),
    });
  };

  const carConflictInfo = useMemo(() => getCarConflictInfo(filteredData, carIndex), [filteredData, carIndex]);

  const loadBusInspectionKeys = async (dateRange = {}, companyId = "") => {
    if (!companyId || !dateRange.from || !dateRange.to || isTentativeMode) {
      setBusInspectionKeys(new Set());
      return new Set();
    }

    const { data, error } = await supabase
      .from("bus_inspection_reservations")
      .select("inspection_date, car_number")
      .eq("company", companyId)
      .gte("inspection_date", dateRange.from)
      .lte("inspection_date", dateRange.to)
      .range(0, 4999);

    if (error) {
      console.warn("버스 검사 예약 조회 실패:", error.message || error);
      setBusInspectionKeys(new Set());
      return new Set();
    }

    const keys = new Set(
      (data || [])
        .map((row) => {
          const date = String(row.inspection_date || "").slice(0, 10);
          const carKey = getCarNumberMatchKey(row.car_number);
          return date && carKey ? `${date}__${carKey}` : "";
        })
        .filter(Boolean)
    );
    setBusInspectionKeys(keys);
    return keys;
  };

  const closeCarAutocomplete = useCallback(() => {
    clearTimeout(carSuggestTimerRef.current);
    setCarSearchList([]);
    setSearchingIndex(null);
  }, []);

  useEffect(() => {
    const closeFromOutsidePointer = (event) => {
      const target = event.target;
      const insideAutocomplete = target?.closest?.("[data-car-autocomplete-root='true']");
      if (!insideAutocomplete) closeCarAutocomplete();
    };

    const closeFromEscape = (event) => {
      if (event.key === "Escape") closeCarAutocomplete();
    };

    document.addEventListener("pointerdown", closeFromOutsidePointer, true);
    document.addEventListener("keydown", closeFromEscape, true);
    return () => {
      document.removeEventListener("pointerdown", closeFromOutsidePointer, true);
      document.removeEventListener("keydown", closeFromEscape, true);
    };
  }, [closeCarAutocomplete]);

  useEffect(() => {
    setFilteredData([]);
    setCopySelectedIds(new Set());
    if (ui.state.applied) fetchData(ui.state.applied);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reservationMode, type]);

  const fetchData = async (basisOverride = ui.state.applied) => {
    const basis = basisOverride;
    if (!basis) return;
    const { dateRange, filters, sortOrder } = basis;
    const companyId = localStorage.getItem("email");
    if (process.env.NODE_ENV !== "production") console.log("📡 Fetching data for company:", companyId, "Date range:", dateRange);
    let query = supabase
      .from(activeReservationTable)
      .select("*")
      .eq("company", companyId);

      if (process.env.NODE_ENV !== "production") console.log("🔍 Initial query:", query);

    // if (type === "전체") {
    //   // 사용자 정의 순서로 reservation_type 정렬
    //   query = query
    //     .order("date", { ascending: true })
    //     .order("reservation_type", {
    //       ascending: true,
    //     })
    //     .order("user", { ascending: true });
    // } else {
    //   // 기존 정렬 방식 유지
    //   if (sortOrder === "car") {
    //     query = query.order("car_number", { ascending: true });
    //   } else {
    //     query = query.order("date", { ascending: true });
    //   }
    // }
    // ✅ sortOrder에 따라 일관되게 정렬
    if (sortOrder === "car") {
      query = query
        .order("car_number", { ascending: true })
        .order("reservation_type", { ascending: true })
        .order("user", { ascending: true })
        .order("date", { ascending: true });
    } else {
      query = query
        .order("date", { ascending: true })
        .order("reservation_type", { ascending: true })
        .order("user", { ascending: true });
    }

    // 유형에 따라 필터링
    if (type !== "전체") {
      if (type === "일반") query = query.eq("reservation_type", "일반");
      if (type === "통근") query = query.eq("reservation_type", "통근");
      if (type === "투어") query = query.eq("reservation_type", "투어");
      if (type === "학단") query = query.eq("reservation_type", "학단");
    }

    // 날짜 범위
    if (dateRange.from) query = query.gte("date", dateRange.from);
    if (dateRange.to) query = query.lte("date", dateRange.to);

    // 텍스트 필터
    const { 사용자, 출발지, 행선지, 차량, 기사 } = filters;
    if (사용자) query = query.ilike("user", `%${사용자}%`);
    if (출발지) query = query.ilike("start", `%${출발지}%`);
    if (행선지) query = query.ilike("destination", `%${행선지}%`);
    if (차량) query = query.ilike("car_number", `%${차량}%`);
    if (기사) query = query.ilike("human_name", `%${기사}%`);


    const pageSize = 1000;
    let data = [];
    let error = null;

    for (let start = 0; start < DISPATCH_FETCH_LIMIT; start += pageSize) {
      const end = Math.min(start + pageSize - 1, DISPATCH_FETCH_LIMIT - 1);
      const { data: chunk, error: chunkError } = await query.range(start, end);

      if (chunkError) {
        error = chunkError;
        break;
      }

      data = data.concat(chunk || []);
      if (!chunk || chunk.length < pageSize) break;
    }

    if (!error) {
      await loadBusInspectionKeys(dateRange, companyId);
      let sorted = data;

      // if (type === "전체") {
      //   const reservationTypeOrder = {
      //     "일반": 1,
      //     "학단": 2,
      //     "투어": 3,
      //     "통근": 4,
      //   };

      //   sorted = data.sort((a, b) => {
      //     const dateA = new Date(a.date);
      //     const dateB = new Date(b.date);
      //     if (dateA < dateB) return -1;
      //     if (dateA > dateB) return 1;

      //     const typeA = reservationTypeOrder[a.reservation_type] || 999;
      //     const typeB = reservationTypeOrder[b.reservation_type] || 999;
      //     if (typeA < typeB) return -1;
      //     if (typeA > typeB) return 1;

      //     return (a.user || "").localeCompare(b.user || "");
      //   });
      // }
      // 구분으로 먼저 묶고, 선택한 기준과 사용자/노선명 다음에 출발시간순으로 정렬한다.
      sorted = [...data].sort((a, b) => {
        const t = KOREAN_COLLATOR.compare(a.reservation_type || "", b.reservation_type || "");
        if (t !== 0) return t;
        if (sortOrder === "car") {
          const c = KOREAN_COLLATOR.compare(a.car_number || "", b.car_number || "");
          if (c !== 0) return c;
        } else {
          const d = new Date(a.date) - new Date(b.date);
          if (d !== 0) return d;
        }
        const u = KOREAN_COLLATOR.compare(a.user || "", b.user || "");
        if (u !== 0) return u;
        const timeDiff = getTimeSortValue(a.time) - getTimeSortValue(b.time);
        if (timeDiff !== 0) return timeDiff;
        return String(a.date || "").localeCompare(String(b.date || ""));
      });
      // fetchData 성공 시
      const map = new Map();
      sorted.forEach(r => map.set(r.id, { ...r }));
      setOriginalMap(map);

      setFilteredData(sorted);
      setCopySelectedIds(new Set());
    }else{
      console.error("Supabase 데이터 가져오기 오류:", error.message);
      setFilteredData([]);    // ✅ 화면도 비움
      setBusInspectionKeys(new Set());
    }
  };

  const handleFilter = () => {
    // const result = allData.filter((row) => {
    //   return Object.entries(filters).every(([key, value]) => {
    //     const mapKey = {
    //       사용자: "user",
    //       출발지: "start",
    //       행선지: "destination",
    //       투어번호: "tourNumber",
    //       차량: "car_number",
    //       기사: "human_name",
    //     }[key];
    //     return value === "" || (row[mapKey] || "").toString().includes(value);
    //   });
    // });
    // setFilteredData(result); // 🔥 전체 데이터에서 필터링
    // fetchData();
    if (!validateNativeDateInputs([fromDateInputRef.current, toDateInputRef.current])) return;

    const { dateRange, filters, sortOrder } = ui.state;
    const nextDateRange = normalizeDateRange(dateRange);
    if (getInclusiveDateSpanDays(nextDateRange) > 3 && !hasDispatchSearchKeyword(filters)) {
      showUserAlert("3일 이상 검색을 하시고 싶으시다면, 검색어를 꼭 넣어주세요.");
      return;
    }

    const applied = { dateRange: nextDateRange, filters, sortOrder, ts: Date.now() };
    ui.setPartial({ dateRange: nextDateRange, applied });
    fetchData(applied);
  };

  const toggleCopySelection = (id) => {
    setCopySelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const visibleRowIds = filteredData.map((row) => row.id).filter(Boolean);
  const allVisibleRowsSelected =
    visibleRowIds.length > 0 && visibleRowIds.every((id) => copySelectedIds.has(id));

  const toggleAllCopySelection = () => {
    setCopySelectedIds((prev) => {
      const next = new Set(prev);

      if (allVisibleRowsSelected) {
        visibleRowIds.forEach((id) => next.delete(id));
      } else {
        visibleRowIds.forEach((id) => next.add(id));
      }

      return next;
    });
  };

  const openCopyModal = () => {
    if (copySelectedIds.size === 0 || copying) return;
    setCopyDateMode("single");
    setCopyTargetDate("");
    setCopyRangeStartDate("");
    setCopyRangeEndDate("");
    setCopyDateFilter("all");
    setCopyModalOpen(true);
  };

  const closeCopyModal = () => {
    if (copying) return;
    setCopyModalOpen(false);
    setCopyDateMode("single");
    setCopyTargetDate("");
    setCopyRangeStartDate("");
    setCopyRangeEndDate("");
    setCopyDateFilter("all");
  };

  const getCopyTargetDates = async () => {
    if (copyDateMode === "single") {
      if (!copyTargetDate) {
        showUserAlert("복사할 날짜를 선택해 주세요.");
        return null;
      }
      return [copyTargetDate];
    }

    if (!copyRangeStartDate || !copyRangeEndDate) {
      showUserAlert("복사할 시작 날짜와 종료 날짜를 선택해 주세요.");
      return null;
    }
    if (copyRangeStartDate > copyRangeEndDate) {
      showUserAlert("복사 시작 날짜가 종료 날짜보다 늦을 수 없습니다.");
      return null;
    }

    let targetDates = getDatesBetween(copyRangeStartDate, copyRangeEndDate);
    if (copyDateFilter === "weekendOnly") {
      targetDates = targetDates.filter(isWeekendDate);
    } else if (copyDateFilter === "excludeWeekend") {
      const company = localStorage.getItem("email");
      const { data, error } = await supabase
        .from("holidays")
        .select("date")
        .eq("company", company)
        .gte("date", copyRangeStartDate)
        .lte("date", copyRangeEndDate);

      if (error) throw error;
      const holidaySet = new Set((data || []).map((holiday) => String(holiday.date).slice(0, 10)));
      targetDates = targetDates.filter((date) => !isWeekendDate(date) && !holidaySet.has(date));
    }

    if (targetDates.length === 0) {
      showUserAlert("조건에 맞는 복사 날짜가 없습니다.");
      return null;
    }

    return targetDates;
  };

  const handleBulkCopy = async () => {
    if (copying) return;

    const selectedRows = filteredData.filter((row) => copySelectedIds.has(row.id));
    if (selectedRows.length === 0) {
      showUserAlert("복사할 예약을 선택해 주세요.");
      return;
    }

    setCopying(true);
    let copyStage = "복사 날짜 확인";
    try {
      const targetDates = await getCopyTargetDates();
      if (!targetDates) {
        setCopying(false);
        return;
      }

      const copyCount = selectedRows.length * targetDates.length;
      if (copyCount > COPY_BATCH_LIMIT) {
        showUserAlert(`한 번에 복사할 수 있는 예약은 ${COPY_BATCH_LIMIT}건까지입니다. 복사 날짜를 나눠 선택해 주세요.`);
        return;
      }

      copyStage = "기존 노선 확인";
      const selectedUsers = Array.from(
        new Set(selectedRows.map((row) => String(row.user || "").trim()).filter(Boolean))
      );
      if (selectedUsers.length > 0) {
        const duplicateRouteExists = await hasExistingCopyRoute(
          activeReservationTable, localStorage.getItem("email"), targetDates, selectedUsers
        );
        if (duplicateRouteExists) {
          const ok = window.confirm("이미 해당 일자에 해당 노선이 있습니다. 그래도 복사할까요?");
          if (!ok) return;
        }
      }

      const rowsToInsert = [];
      targetDates.forEach((targetDate) => {
        selectedRows.forEach((row) => {
          const {
            id,
            created_at,
            updated_at,
            inserted_at,
            modified_at,
            ...copyRow
          } = row;
          const copiedGroupId = createGroupId();

          rowsToInsert.push({
            ...copyRow,
            date: targetDate,
            driver_check: false,
            ...(!isTentativeMode
              ? {
                  driver_sms_status: null,
                  driver_sms_sent_at: null,
                  driver_sms_failed_at: null,
                  driver_sms_error: null,
                }
              : {}),
            round_trip_time: copyRow.one_round === "왕복" ? copyRow.round_trip_time || null : null,
            reservation_group_id: copiedGroupId,
          });
        });
      });

      if (!(await ensureNoDriverHolidayConflict(rowsToInsert))) {
        return;
      }

      const summaryRowsToInsert = rowsToInsert.map((row) => ({
          group_id: row.reservation_group_id,
          start_date: row.date,
          end_date: row.date,
          days: Number(row.days) || 0,
          time: row.time || null,
          arrival_time: row.arrival_time || null,
          round_trip_time: row.one_round === "왕복" ? row.round_trip_time || null : null,
          number_of_car: Number(row.number_of_car) || 1,
          user: row.user || null,
          user_phone: row.user_phone || null,
          naver_map_url: row.naver_map_url || null,
          reservation_type: row.reservation_type || null,
          car_type: row.seat || null,
          start: row.start || null,
          destination: row.destination || null,
          transit: row.transit || null,
          money: Number(row.money) || 0,
          driver_money: Number(row.driver_money) || 0,
          company: row.company || localStorage.getItem("email"),
        }));


      copyStage = "예약 및 요약 등록";
      const { data: insertedCount, error } = await createReservationBatch(
        rowsToInsert, summaryRowsToInsert, activeReservationTable
      );
      if (error) {
        console.error("배차 복사 실패", { stage: copyStage, code: error.code, status: error.status, error });
        showUserAlert(getCopyFailureMessage(error));
        return;
      }
      if (Number(insertedCount) !== rowsToInsert.length) {
        throw new Error("서버에서 복사된 예약 수를 확인하지 못했습니다.");
      }

      const dateMessage = copyDateMode === "single"
        ? targetDates[0]
        : `${targetDates[0]} ~ ${targetDates[targetDates.length - 1]} 중 ${targetDates.length}일`;
      showUserAlert(`${selectedRows.length}개 ${reservationMode}을 ${dateMessage}에 총 ${rowsToInsert.length}건 복사했습니다.`);
      setCopyModalOpen(false);
      setCopyDateMode("single");
      setCopyTargetDate("");
      setCopyRangeStartDate("");
      setCopyRangeEndDate("");
      setCopyDateFilter("all");
      setCopySelectedIds(new Set());
      await fetchData();
    } catch (error) {
      console.error("배차 복사 실패", { stage: copyStage, code: error?.code, status: error?.status, error });
      showUserAlert(getCopyFailureMessage(error));
    } finally {
      setCopying(false);
    }
  };

  const handleConfirmTentativeReservations = async () => {
    if (!isTentativeMode || confirmingTentative || copySelectedIds.size === 0) return;

    const selectedCount = copySelectedIds.size;
    const ok = window.confirm(`${selectedCount}개 가예약을 예약으로 확정하시겠습니까?`);
    if (!ok) return;

    setConfirmingTentative(true);
    try {
      const selectedRows = filteredData.filter((row) => copySelectedIds.has(row.id));
      if (selectedRows.length === 0) {
        showUserAlert("확정할 가예약을 선택해 주세요.");
        return;
      }

      const rowsToInsert = selectedRows.map((row) => {
        const {
          id,
          created_at,
          updated_at,
          inserted_at,
          modified_at,
          ...reservationRow
        } = row;
        return reservationRow;
      });

      if (!(await ensureNoDriverHolidayConflict(rowsToInsert))) {
        return;
      }

      const selectedIds = selectedRows.map((row) => row.id).filter(Boolean);
      const { error: confirmError } = await supabase.rpc("confirm_tentative_reservations", {
        p_ids: selectedIds.map(String),
      });
      if (confirmError) throw confirmError;

      showUserAlert(`${rowsToInsert.length}개 가예약을 예약으로 확정했습니다.`);
      setCopySelectedIds(new Set());
      await fetchData();
    } catch (e) {
      console.error("가예약 확정 실패:", e);
      showUserAlert("예약 확정 실패: " + (e.message || String(e)));
    } finally {
      setConfirmingTentative(false);
    }
  };

  const handleBulkDelete = async () => {
    if (deleting || copySelectedIds.size === 0) return;

    const selectedCount = copySelectedIds.size;
    const ok = window.confirm(`${selectedCount}개 예약을 삭제하시겠습니까?`);
    if (!ok) return;

    setDeleting(true);
    try {
      const selectedIds = Array.from(copySelectedIds);
      const chunkSize = 100;
      const company = localStorage.getItem("email") || "";

      for (let i = 0; i < selectedIds.length; i += chunkSize) {
        const idChunk = selectedIds.slice(i, i + chunkSize);
        const { error } = await supabase
          .from(activeReservationTable)
          .delete()
          .eq("company", company)
          .in("id", idChunk);

        if (error) {
          throw error;
        }
      }

      showUserAlert(`${selectedCount}개 ${reservationMode}을 삭제했습니다.`);
      setCopySelectedIds(new Set());
      await fetchData();
    } catch (error) {
      showUserAlert("삭제 실패: " + (error?.message || String(error)));
    } finally {
      setDeleting(false);
    }
  };

  const openDispatchRuleModal = async () => {
    const company = localStorage.getItem("email");
    if (!company) {
      showUserAlert("회사 정보를 찾을 수 없습니다. 다시 로그인해 주세요.");
      return;
    }
    if (filteredData.length === 0) {
      showUserAlert("배차규칙을 적용할 조회 결과가 없습니다. 먼저 검색해 주세요.");
      return;
    }

    setDispatchRuleOpen(true);
    setDispatchRuleLoading(true);
    setDispatchRulePreview([]);
    setSelectedDispatchPreviewIds(new Set());
    setDispatchRuleStartDriverIds({});

    const { data, error } = await supabase
      .from("dispatch_rules")
      .select("*, dispatch_rule_drivers(*), dispatch_rule_routes(*)")
      .eq("company", company)
      .order("priority", { ascending: true })
      .order("created_at", { ascending: false });

    setDispatchRuleLoading(false);

    if (error) {
      showUserAlert("배차 규칙을 불러오지 못했습니다: " + error.message);
      setDispatchRules([]);
      return;
    }

    const nextRules = data || [];
    setDispatchRules(nextRules);
    setSelectedDispatchRuleIds(new Set(nextRules.filter((rule) => rule.enabled !== false).map((rule) => rule.id)));
    setDispatchRuleStartDriverIds(await buildRotationStartDriverDefaults(nextRules, filteredData));
  };

  const closeDispatchRuleModal = () => {
    if (dispatchRuleApplying) return;
    setDispatchRuleOpen(false);
  };

  const toggleDispatchRule = (ruleId) => {
    setSelectedDispatchRuleIds((prev) => {
      const next = new Set(prev);
      if (next.has(ruleId)) next.delete(ruleId);
      else next.add(ruleId);
      return next;
    });
  };

  const updateDispatchRuleStartDriver = (ruleId, driverId) => {
    setDispatchRuleStartDriverIds((prev) => ({ ...prev, [ruleId]: driverId }));
  };

  const buildRotationStartDriverDefaults = async (rules = [], rows = []) => {
    const company = localStorage.getItem("email");
    const rotationRules = rules.filter((rule) => rule.enabled !== false && rule.rule_type === "rotation");
    if (!company || rotationRules.length === 0) return {};

    const defaults = {};
    rotationRules.forEach((rule) => {
      const drivers = getDispatchRuleDrivers(rule);
      const savedDriverId = rule.options_json?.rotation_start_driver_id;
      defaults[rule.id] = savedDriverId || drivers[0]?.driver_profile_id || drivers[0]?.driver_name || "";
    });

    const targetDates = rows.map((row) => row.date).filter(Boolean);
    if (targetDates.length === 0) return defaults;

    const minTargetDate = targetDates.reduce((min, date) => (date < min ? date : min), targetDates[0]);
    const { data, error } = await supabase
      .from("dispatch_rule_history")
      .select("rule_id, reservation_date, driver_profile_id, driver_name, created_at")
      .eq("company", company)
      .eq("action_type", "apply")
      .in("rule_id", rotationRules.map((rule) => rule.id))
      .lt("reservation_date", minTargetDate)
      .order("reservation_date", { ascending: false })
      .order("created_at", { ascending: false })
      .limit(1000);

    if (error) {
      console.warn("순번제 시작 기사 이력 조회 실패:", error.message);
      return defaults;
    }

    const latestByRule = new Map();
    (data || []).forEach((row) => {
      if (row.rule_id && !latestByRule.has(row.rule_id)) latestByRule.set(row.rule_id, row);
    });

    rotationRules.forEach((rule) => {
      const latest = latestByRule.get(rule.id);
      if (!latest) return;

      const drivers = getDispatchRuleDrivers(rule);
      const latestIndex = drivers.findIndex((driver) => {
        if (latest.driver_profile_id && driver.driver_profile_id === latest.driver_profile_id) return true;
        return normalizeDriverName(driver.driver_name) === normalizeDriverName(latest.driver_name);
      });

      if (latestIndex >= 0 && drivers.length > 0) {
        const nextDriver = drivers[(latestIndex + 1) % drivers.length];
        defaults[rule.id] = nextDriver?.driver_profile_id || nextDriver?.driver_name || "";
      }
    });

    return defaults;
  };

  const toggleDispatchPreview = (previewId) => {
    setSelectedDispatchPreviewIds((prev) => {
      const next = new Set(prev);
      if (next.has(previewId)) next.delete(previewId);
      else next.add(previewId);
      return next;
    });
  };

  const toggleAllDispatchPreview = () => {
    setSelectedDispatchPreviewIds((prev) => {
      if (dispatchRulePreview.length > 0 && dispatchRulePreview.every((row) => prev.has(row.previewId))) {
        return new Set();
      }
      return new Set(dispatchRulePreview.map((row) => row.previewId));
    });
  };

  const buildDispatchRulePreview = async ({ randomize = false } = {}) => {
    const selectedRules = dispatchRules.filter((rule) => selectedDispatchRuleIds.has(rule.id));
    if (selectedRules.length === 0) {
      showUserAlert("적용할 배차 규칙을 선택해 주세요.");
      return;
    }

    setDispatchRuleLoading(true);
    try {
      const historyRows = await fetchDispatchRuleWeightHistory(filteredData, selectedRules);
      const initialAssignedCounts = createRollingWeightInitialCounts(filteredData, selectedRules, historyRows);
      const previewRows = createDispatchRulePreview(filteredData, selectedRules, driverIndex, {
        randomizeBalanced: randomize,
        initialAssignedCounts,
        rotationStartDriverIds: dispatchRuleStartDriverIds,
      });
      setDispatchRulePreview(previewRows);
      setSelectedDispatchPreviewIds(new Set(previewRows.map((row) => row.previewId)));

      if (previewRows.length === 0) {
        showUserAlert("현재 조회 결과에 적용 가능한 배차 규칙이 없습니다.");
      }
    } catch (error) {
      showUserAlert("배차규칙 가중치 조회 실패: " + (error.message || error));
    } finally {
      setDispatchRuleLoading(false);
    }
  };

  const applyDispatchRulePreview = async () => {
    if (dispatchRuleApplying) return;

    const selectedRows = dispatchRulePreview.filter((row) => selectedDispatchPreviewIds.has(row.previewId));
    if (selectedRows.length === 0) {
      showUserAlert("적용할 항목을 선택해 주세요.");
      return;
    }

    if (!window.confirm(`${selectedRows.length}개 항목에 배차규칙을 적용할까요?`)) return;

    setDispatchRuleApplying(true);
    try {
      const assignmentRows = selectedRows
        .filter((row) => row.actionType !== "clear")
        .map((row) => ({
          date: row.date,
          car_number: row.recommendedCarNumber || "",
          human_name: row.recommendedDriverName || "",
          phone: row.recommendedPhone || "",
        }));

      if (!(await ensureNoDriverHolidayConflict(assignmentRows))) {
        return;
      }

      for (const row of selectedRows) {
        if (!row.reservationId) {
          throw new Error("예약 ID가 없는 미리보기 항목이 있어 적용할 수 없습니다.");
        }
        const updatePayload = row.actionType === "clear"
          ? {
              car_number: null,
              human_name: null,
              phone: null,
              driver_email: null,
              driver_check: false,
            }
          : {
              car_number: row.recommendedCarNumber || null,
              human_name: row.recommendedDriverName || null,
              phone: row.recommendedPhone || null,
              driver_email: row.recommendedEmail || null,
              driver_check: false,
            };
        const { error } = await supabase
          .from(activeReservationTable)
          .update(updatePayload)
          .eq("id", row.reservationId)
          .eq("company", localStorage.getItem("email"));
        if (error) throw error;

        await supabase.from("dispatch_rule_history").insert({
          company: localStorage.getItem("email"),
          rule_id: row.ruleId,
          reservation_id: row.reservationId,
          reservation_date: row.date || null,
          route_user: row.routeUserRaw || row.routeUser || null,
          driver_name: row.actionType === "clear" ? row.currentDriverName || null : row.recommendedDriverName || null,
          car_number: row.actionType === "clear" ? row.currentCarNumber || null : row.recommendedCarNumber || null,
          driver_profile_id: row.actionType === "clear" ? null : row.recommendedDriverProfileId || null,
          action_type: row.actionType === "clear" ? "clear_conflict" : "apply",
          reason: row.reason || null,
        });
      }

      showUserAlert("배차규칙이 적용되었습니다.");
      setDispatchRuleOpen(false);
      await fetchData();
    } catch (error) {
      showUserAlert("배차규칙 적용 실패: " + (error.message || error));
    } finally {
      setDispatchRuleApplying(false);
    }
  };

  const openUnassignedStatus = async () => {
    const company = localStorage.getItem("email");
    const serviceDate = ui.state.dateRange.from;

    if (!company) {
      showUserAlert("회사 정보를 찾을 수 없습니다. 다시 로그인해 주세요.");
      return;
    }

    if (!serviceDate) {
      showUserAlert("조회 기준 운행일이 없습니다.");
      return;
    }

    const popup = window.open("", "unassignedStatus", "width=820,height=720,scrollbars=yes,resizable=yes");
    if (!popup) {
      showUserAlert("팝업이 차단되었습니다. 브라우저에서 팝업 허용 후 다시 시도해 주세요.");
      return;
    }

    const { data: sessionData } = await supabase.auth.getSession();
    const accessToken = sessionData?.session?.access_token || "";

    writeUnassignedStatusPopup(popup, serviceDate, [], true, "", accessToken);
    setUnassignedLoading(true);

    const [year, month] = serviceDate.split("-").map(Number);
    const monthStart = `${serviceDate.slice(0, 7)}-01`;
    const monthEndDate = new Date(year, month, 0);
    const monthEnd = `${year}-${String(month).padStart(2, "0")}-${String(monthEndDate.getDate()).padStart(2, "0")}`;

    const [carsResult, reservationsResult, profilesResult] = await Promise.all([
      supabase
        .from("cars")
        .select("car_number_only, driver, phone, direct")
        .eq("company", company)
        .eq("direct", true)
        .order("car_number_only", { ascending: true }),
      supabase
        .from("reservations")
        .select("car_number, one_round, user")
        .eq("company", company)
        .eq("date", serviceDate),
      supabase
        .from("profiles")
        .select("id, driver_name, phone, memo")
        .eq("company", company)
        .eq("role", "driver"),
    ]);

    if (carsResult.error) {
      setUnassignedLoading(false);
      showUserAlert("차량 정보를 불러오지 못했습니다: " + carsResult.error.message);
      writeUnassignedStatusPopup(popup, serviceDate, [], false, carsResult.error.message, accessToken);
      return;
    }

    if (reservationsResult.error) {
      setUnassignedLoading(false);
      showUserAlert("예약 정보를 불러오지 못했습니다: " + reservationsResult.error.message);
      writeUnassignedStatusPopup(popup, serviceDate, [], false, reservationsResult.error.message, accessToken);
      return;
    }

    if (profilesResult.error) {
      setUnassignedLoading(false);
      showUserAlert("기사 메모 정보를 불러오지 못했습니다: " + profilesResult.error.message);
      writeUnassignedStatusPopup(popup, serviceDate, [], false, profilesResult.error.message, accessToken);
      return;
    }

    const monthlyRows = [];
    let monthlyError = null;
    const pageSize = 1000;
    for (let from = 0; ; from += pageSize) {
      const to = from + pageSize - 1;
      const { data, error } = await supabase
        .from("reservations")
        .select("id, date, car_number, human_name")
        .eq("company", company)
        .gte("date", monthStart)
        .lte("date", monthEnd)
        .order("date", { ascending: true })
        .order("id", { ascending: true })
        .range(from, to);

      if (error) {
        monthlyError = error;
        break;
      }

      monthlyRows.push(...(data || []));
      if (!data || data.length < pageSize) break;
    }

    setUnassignedLoading(false);

    if (monthlyError) {
      showUserAlert("월간 운행일수를 불러오지 못했습니다: " + monthlyError.message);
      writeUnassignedStatusPopup(popup, serviceDate, [], false, monthlyError.message, accessToken);
      return;
    }

    const monthlyDatesByCar = new Map();
    monthlyRows.forEach((reservation) => {
      const carKey = getCarNumberMatchKey(reservation.car_number);
      if (!carKey || !reservation.date) return;

      if (!monthlyDatesByCar.has(carKey)) monthlyDatesByCar.set(carKey, new Set());
      monthlyDatesByCar.get(carKey).add(reservation.date);
    });

    const dispatchInfo = new Map();
    (reservationsResult.data || []).forEach((reservation) => {
      const carKey = getCarNumberMatchKey(reservation.car_number);
      if (!carKey) return;

      const count = reservation.one_round === "왕복" ? 2 : 1;
      const info = dispatchInfo.get(carKey) || { count: 0, routes: [] };
      info.count += count;
      if (count === 1 && reservation.user) info.routes.push(reservation.user);
      dispatchInfo.set(carKey, info);
    });

    const profileByPhone = new Map();
    const profileByName = new Map();
    (profilesResult.data || []).forEach((profile) => {
      const phoneKey = normalizePhone(profile.phone);
      const nameKey = normalizeDriverName(profile.driver_name);
      if (phoneKey) profileByPhone.set(phoneKey, profile);
      if (nameKey && !profileByName.has(nameKey)) profileByName.set(nameKey, profile);
    });

    const rows = (carsResult.data || [])
      .map((car) => {
        const carKey = getCarNumberMatchKey(car.car_number_only);
        const info = dispatchInfo.get(carKey) || { count: 0, routes: [] };
        const count = info.count;
        const profile =
          profileByPhone.get(normalizePhone(car.phone)) ||
          profileByName.get(normalizeDriverName(car.driver)) ||
          null;
        return {
          car_number: car.car_number_only || "",
          count,
          countLabel: count === 1 && info.routes.length > 0 ? `1(${info.routes.join(", ")})` : String(count),
          driver: car.driver || "",
          monthlyWorkDays: monthlyDatesByCar.get(carKey)?.size || 0,
          phone: car.phone || "",
          profileId: profile?.id || "",
          memo: profile?.memo || "",
        };
      })
      .sort((a, b) => a.count - b.count || String(a.car_number).localeCompare(String(b.car_number)));

    writeUnassignedStatusPopup(popup, serviceDate, rows, false, "", accessToken);
  };

  const debounce = (fn, ms=150) => (...args) => {
    clearTimeout(carSuggestTimerRef.current);
    carSuggestTimerRef.current = setTimeout(() => fn(...args), ms);
  };

  const selectCarSuggestion = async (index, car) => {
    const updated = [...filteredData];
    const nextRow = {
      ...updated[index],
      car_number: car.car_number_only || "",
      human_name: car.driver || "",
      phone: car.phone || "",
      driver_email: car.driver_email || "",
    };
    if (await hasDriverHolidayConflictForRow(nextRow)) {
      updated[index] = clearDriverAssignment(nextRow);
      setFilteredData(updated);
      closeCarAutocomplete();
      return;
    }
    updated[index] = nextRow;
    setFilteredData(updated);
    closeCarAutocomplete();
  };

  const syncDriverContactByName = (row, driverName) => {
    const trimmedName = String(driverName || "").trim();
    if (!trimmedName) {
      return {
        ...row,
        car_number: "",
        phone: "",
        driver_email: "",
      };
    }

    const driver = driverIndex.get(trimmedName);
    if (!driver) return row;
    if (driver.duplicate) return row;

    return {
      ...row,
      car_number: row.car_number || driver.car_number || "",
      phone: driver.phone || "",
      driver_email: driver.email || row.driver_email || "",
    };
  };

  const handleChange = async (index, field, value) => {
    const updated = [...filteredData];
    updated[index][field] = value;

    if (field === "car_number") {
      if (!String(value || "").trim()) {
        clearTimeout(carSuggestTimerRef.current);
        updated[index]["car_number"] = "";
        updated[index]["human_name"] = "";
        updated[index]["phone"] = "";
        updated[index]["driver_email"] = "";
        setCarSearchList([]);
        setSearchingIndex(null);
        setFilteredData(updated);
        return;
      }

      // 4자리 완성되면 기사/전화/이메일 채우기
      if (/^\d{4}$/.test((value || "").trim())) {
        clearTimeout(carSuggestTimerRef.current);
        const c = carIndex.get(value.trim());
        if (c) {
          const nextRow = {
            ...updated[index],
            human_name: c.driver || "",
            phone: c.phone || "",
            driver_email: c.driver_email || "",
          };
          if (await hasDriverHolidayConflictForRow(nextRow)) {
            updated[index] = clearDriverAssignment(nextRow);
            setCarSearchList([]);
            setSearchingIndex(null);
            setFilteredData(updated);
            return;
          }
          updated[index] = nextRow;
          setCarSearchList([]); // 확정되면 닫기
          setSearchingIndex(null);
        } else {
          // 캐시에 없음 → 초기화
          updated[index]["human_name"] = "";
          updated[index]["phone"] = "";
          updated[index]["driver_email"] = "";
          setCarSearchList([]);
          setSearchingIndex(null);
        }
      } else {
        // 자동완성: 캐시에서 필터링 (DB 호출 X)
        setSearchingIndex(index);
        debounce(() => {
          const q = (value || "").trim();
          if (!q) { setCarSearchList([]); setSearchingIndex(null); return; }
          const filtered = carListAll
            .filter(c => (c.car_number_only || "").startsWith(q));
          setCarSearchList(filtered);
          setSearchingIndex(index);
        })();

        // 4자리 전에는 기사/연락처 미채움
        updated[index]["human_name"] = updated[index]["human_name"] || "";
        updated[index]["phone"] = updated[index]["phone"] || "";
        updated[index]["driver_email"] = updated[index]["driver_email"] || "";
      }
    }

    if (field === "human_name") {
      if (!String(value || "").trim()) {
        updated[index]["car_number"] = "";
        updated[index]["human_name"] = "";
        updated[index]["phone"] = "";
        updated[index]["driver_email"] = "";
        setFilteredData(updated);
        return;
      }

      updated[index] = syncDriverContactByName(updated[index], value);
    }

    setFilteredData(updated);
  };

  const handleUpdate = async () => {
    if (saving || uiLocked) return;
    setSaving(true);

    try {
      // ✅ 1) "이번 저장에서 SMS 보내야 하는 row" 선별
      // 조건: 원본에는 phone 없었는데, 지금은 phone 있고, human_name도 있는 경우(선택)
      const smsTargets = filteredData.filter((row) => {
        const before = originalMap.get(row.id);
        const beforePhone = (before?.phone || "").trim();
        const afterPhone = (row.phone || "").trim();

        // phone이 새로 생긴 경우만
        const phoneAdded = !beforePhone && !!afterPhone;

        // 기사명도 조건 걸고 싶으면(권장)
        const hasName = !!(row.human_name || "").trim();

        return phoneAdded && hasName;
      });

      // ✅ 2) 업데이트(병렬)
      const updateJobs = filteredData.map((row) => {
        const { id, ...data } = row;
        const before = originalMap.get(id);
        const beforeCarNumber = String(before?.car_number || "").trim();
        const afterCarNumber = String(data.car_number || "").trim();
        const beforeDriver = String(before?.human_name || "").trim();
        const afterDriver = String(data.human_name || "").trim();

        if (beforeCarNumber !== afterCarNumber || beforeDriver !== afterDriver) {
          data.driver_check = false;
        }

        data.time = data.time || null;
        data.arrival_time = data.arrival_time || null;
        data.round_trip_time = data.one_round === "왕복" ? data.round_trip_time || null : null;
        return supabase
          .from(activeReservationTable)
          .update(data)
          .eq("id", id)
          .eq("company", localStorage.getItem("email"));
      });

      const results = await Promise.all(updateJobs);
      const firstErr = results.find((r) => r.error)?.error;
      if (firstErr) throw firstErr;

      // ✅ 3) 문자 발송 (업데이트 성공 후에)
      if (SMS_SENDING_PAUSED && smsTargets.length > 0) {
        console.warn("SMS sending is paused. Skipped targets:", smsTargets.length);
      } else {
        for (const row of smsTargets) {
          const { error: smsErr } = await supabase.functions.invoke("send-reservation-sms", {
            body: {
              record: {
                phone: row.phone,                 // 010으로 시작
                human_name: row.human_name,
                date: row.date,
                start: row.start,
                destination: row.destination,
              },
            },
          });

          if (smsErr) {
            console.warn("SMS failed:", row.id, smsErr.message);
          }
        }
      }

      // ✅ UI 잠금/알림
      setUiLocked(true);
      setCarSearchList([]);
      showUserAlert("수정 완료되었습니다.");
      setUiLocked(false);

      // ✅ 다시 조회해서 originalMap 갱신되게
      await fetchData();
      setSaving(false);
    } catch (e) {
      console.error(e);
      showUserAlert("수정 실패: " + (e?.message || "알 수 없는 오류"));
      setSaving(false);
    }
  };

  useEffect(() => {
    const loadCars = async () => {
      const company = localStorage.getItem("email");
      if (!company) return;
      const [{ data, error }, { data: driverData, error: driverError }] = await Promise.all([
        supabase
        .from("cars")
        .select("car_number, car_number_only, driver, phone, driver_email, direct")
        .eq("company", company),
        supabase
          .from("profiles")
          .select("driver_name, phone, email, car_number")
          .eq("company", company)
          .eq("role", "driver"),
      ]);
      let normalizedCars = [];
      if (!error && data) {
        const m = new Map();
        normalizedCars = (data || [])
          .map((c) => ({
            ...c,
            car_number_only: getCarNumberMatchKey(c.car_number_only || c.car_number),
          }))
          .filter((c) => c.car_number_only);

        normalizedCars.forEach(c => {
          const k = (c.car_number_only || "").trim();
          if (k) m.set(k, c);
        });
        setCarIndex(m);
        setCarListAll(normalizedCars);
      }
      if (!driverError && driverData) {
        const drivers = new Map();
        const setUniqueDriver = (name, driver) => {
          if (!name) return;
          if (drivers.has(name)) {
            drivers.set(name, { duplicate: true });
            return;
          }
          drivers.set(name, driver);
        };
        const carByDriver = new Map();
        normalizedCars.forEach((car) => {
          const name = (car.driver || "").trim();
          if (!name) return;
          if (carByDriver.has(name)) {
            carByDriver.set(name, { duplicate: true });
            return;
          }
          carByDriver.set(name, car);
        });

        driverData.forEach((driver) => {
          const name = (driver.driver_name || "").trim();
          const car = carByDriver.get(name);
          const carNumber = car?.duplicate
            ? ""
            : getCarNumberMatchKey(driver.car_number || car?.car_number_only || "");

          setUniqueDriver(name, {
            ...driver,
            phone: driver.phone || car?.phone || "",
            email: driver.email || car?.driver_email || "",
            car_number: carNumber,
          });
        });
        normalizedCars.forEach((car) => {
          const name = (car.driver || "").trim();
          if (!name || drivers.has(name)) return;
          const carMatch = carByDriver.get(name);
          setUniqueDriver(name, {
            driver_name: name,
            phone: carMatch?.phone || "",
            email: carMatch?.driver_email || "",
            car_number: carMatch?.duplicate ? "" : carMatch?.car_number_only || "",
          });
        });
        setDriverIndex(drivers);
      }
    };
    loadCars();
  }, []);

  const handleDateRangeFromChange = (value) => {
    const nextValue = clampDateString(value);
    const currentTo = ui.state.dateRange.to;
    ui.setPartial((prev) => ({
      dateRange: {
        ...prev.dateRange,
        from: nextValue,
        to: currentTo && nextValue && nextValue > currentTo ? nextValue : currentTo,
      },
    }));
  };

  const handleDateRangeToChange = (value) => {
    ui.setPartial((prev) => ({
      dateRange: {
        ...prev.dateRange,
        to: clampDateString(value),
      },
    }));
  };

  const reservationTypeCounts = useMemo(() => {
    return filteredData.reduce(
      (counts, row) => {
        const key = row.reservation_type;
        if (Object.prototype.hasOwnProperty.call(counts, key)) counts[key] += 1;
        return counts;
      },
      { 일반: 0, 학단: 0, 통근: 0, 투어: 0 }
    );
  }, [filteredData]);

  // 📌 여기에 추가: 정렬 라디오 버튼 UI
  const renderSortOptions = () => (
    <div className="flex flex-wrap items-center justify-between gap-3 px-2">
      <div className="flex flex-wrap gap-6 items-center">
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="sortOrder"
            value="date"
            checked={ui.state.sortOrder === "date"}
            onChange={() => ui.setPartial({ sortOrder: "date" })}
          />
          날짜순
        </label>
        <label className="flex items-center gap-2">
          <input
            type="radio"
            name="sortOrder"
            value="car"
            checked={ui.state.sortOrder === "car"}
            onChange={() => ui.setPartial({ sortOrder: "car" })}
          />
          차량번호순
        </label>
        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-700">
          <span className="flex items-center gap-1">
            <span className="inline-block h-4 w-6 rounded border border-gray-300 bg-green-600" />
            주말
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-4 w-6 rounded border border-gray-300" style={{ backgroundColor: "#bfdbfe" }} />
            외부차량
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-4 w-6 rounded border border-gray-300" style={{ backgroundColor: "#f9a8d4" }} />
            출/퇴근 매칭차량
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-4 w-6 rounded border border-gray-300" style={{ backgroundColor: "#f87171" }} />
            오류 매칭
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-4 w-6 rounded border border-gray-300" style={{ backgroundColor: "#4b5563" }} />
            기사 확인
          </span>
          <span className="flex items-center gap-1">
            <span className="inline-block h-4 w-6 rounded border border-gray-300" style={{ backgroundColor: "#fdba74" }} />
            버스 검사
          </span>
        </div>
      </div>
      <div className="ml-auto flex flex-wrap justify-end gap-2 text-sm font-semibold">
        {["일반", "학단", "통근", "투어"].map((reservationType) => (
          <span
            key={reservationType}
            className="inline-flex min-w-[64px] items-center justify-center rounded-full bg-blue-600 px-3 py-1 text-white"
          >
            {reservationType} {reservationTypeCounts[reservationType]}건
          </span>
        ))}
      </div>
    </div>
  );

  const dispatchTableHeaders = isTentativeMode
    ? ["순번", "구분", "운행일", "사용자/노선명", "편도/왕복", "출발시간", "도착시간", "회차시간", "출발지", "경유지", "행선지", "받을금액"]
    : ["순번", "구분", "운행일", "사용자/노선명", "차량번호", "기사명", "기사연락처", "편도/왕복", "시간", "출발지", "경유지", "행선지", "받을금액"];

  return (
    <div className={`px-4 pt-1 pb-4 space-y-3 ${saving || uiLocked ? "pointer-events-none opacity-60" : ""}`}>
      <h2 className="text-lg font-semibold">{type} 배차처리 {isTentativeMode ? "(가예약)" : ""}</h2>

      <div className="space-y-2 no-print md:flex md:flex-wrap md:items-center md:gap-2 md:space-y-0">
        <div className="flex w-full items-center gap-1 md:w-auto md:gap-2">
          <span className="shrink-0 text-xs md:text-sm">운행일:</span>
          <Input
            ref={fromDateInputRef}
            type="date"
            value={ui.state.dateRange.from || ""}
            onChange={(e) => handleDateRangeFromChange(e.target.value)}
            className="min-w-0 w-0 flex-1 px-1 text-xs md:w-auto md:flex-none md:px-2 md:text-sm"
            aria-label="운행일 시작일"
          />
          <span className="shrink-0 text-xs md:text-sm">~</span>
          <Input
            ref={toDateInputRef}
            type="date"
            value={ui.state.dateRange.to || ""}
            onChange={(e) => handleDateRangeToChange(e.target.value)}
            className="min-w-0 w-0 flex-1 px-1 text-xs md:w-auto md:flex-none md:px-2 md:text-sm"
            aria-label="운행일 종료일"
          />
        </div>

        <div className="flex w-full items-center gap-2 md:hidden">
          <select
            value={mobileFilterKey}
            onChange={(event) => handleMobileFilterKeyChange(event.target.value)}
            className="w-[112px] shrink-0 rounded border border-gray-300 bg-white px-1 py-1.5 text-xs"
            aria-label="모바일 검색 조건"
          >
            {MOBILE_DISPATCH_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
          <Input
            className="min-w-0 flex-1 text-sm"
            value={ui.state.filters?.[mobileFilterKey] || ""}
            placeholder={`${MOBILE_DISPATCH_FILTER_OPTIONS.find((option) => option.value === mobileFilterKey)?.label || "검색어"} 검색`}
            onChange={(event) => handleMobileFilterTextChange(event.target.value)}
            aria-label="모바일 검색어"
          />
          <Button
            className="shrink-0 px-3 py-1.5 text-sm"
            onClick={handleFilter}
            disabled={saving || uiLocked}
          >검색</Button>
        </div>

        <div className="hidden flex-wrap items-center gap-2 md:flex">
          <Input className="w-40" placeholder="사용자/노선명" {...ui.bind("filters.사용자")} />
          <Input className="w-40" placeholder="출발지" {...ui.bind("filters.출발지")} />
          <Input className="w-40" placeholder="행선지" {...ui.bind("filters.행선지")} />
          <Input className="w-40" placeholder="차량" {...ui.bind("filters.차량")} />
          <Input className="w-40" placeholder="기사" {...ui.bind("filters.기사")} />
          <Button onClick={handleFilter} disabled={saving || uiLocked}>검색</Button>
        </div>
      </div>

      {/* ✅ 정렬 라디오 버튼 */}
      <div className="hidden md:block no-print">{renderSortOptions()}</div>

      {/* 🖨️ 프린트 전용 머리말: 제목 + 조회기간 */}
      <div className="print-only mb-3">
        <div className="text-xl font-bold">{type} 배차처리</div>
        <div className="text-sm text-gray-700">
          운행일: {ui.state.dateRange.from} ~ {ui.state.dateRange.to}
        </div>
      </div>

      <Card>
        <CardContent className="bg-white">
          <div className="max-h-[70vh] w-full max-w-full overflow-auto">
            <table className="dispatch-mobile-table table-auto w-full border border-gray-300 text-sm whitespace-nowrap print-table">
              <thead className="bg-blue-500 text-white">
                <tr>
                  {/* ✅ '복사' 선택 열 */}
                  <th className="sticky top-0 z-20 border border-gray-300 bg-blue-500 px-1.5 py-1 print-hide hidden md:table-cell">
                    <input
                      type="checkbox"
                      checked={allVisibleRowsSelected}
                      ref={(element) => {
                        if (element) {
                          element.indeterminate =
                            copySelectedIds.size > 0 && !allVisibleRowsSelected;
                        }
                      }}
                      onChange={toggleAllCopySelection}
                      disabled={visibleRowIds.length === 0}
                      aria-label="전체 선택"
                    />
                  </th>
                  {dispatchTableHeaders.map((header) => (
                    <th
                      key={header}
                      className={`sticky top-0 z-20 border border-gray-300 bg-blue-500 ${header === "편도/왕복" ? "px-1 py-1 w-[48px] max-w-[48px] text-xs" : "px-1.5 py-1"} ${hiddenOnMobile(header)} ${isPrintHiddenHeader(header) ? "print-hide" : ""}`}
                    >
                      {header}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredData.map((row, index) => {
                  const mobileRowKey = getRowKey(row, index);
                  const isMobileExpanded = mobileExpandedRows.has(mobileRowKey);
                  const conflictLevel = carConflictInfo.rowLevels.get(mobileRowKey);
                  const busInspectionCarKey = getCarNumberMatchKey(row.car_number);
                  const isBusInspectionRow =
                    !isTentativeMode &&
                    row.date &&
                    busInspectionCarKey &&
                    busInspectionKeys.has(`${String(row.date).slice(0, 10)}__${busInspectionCarKey}`);
                  const displayLevel =
                    conflictLevel === "overlap"
                      ? "overlap"
                      : isBusInspectionRow
                      ? "inspection"
                      : conflictLevel;
                  const rowClassName =
                    displayLevel === "external"
                      ? "bg-blue-200"
                      : displayLevel === "overlap"
                      ? "bg-red-300"
                      : displayLevel === "inspection"
                      ? "bg-orange-200"
                      : displayLevel === "same-day"
                      ? "bg-pink-100"
                      : "";
                  const rowStyle = getConflictRowStyle(displayLevel);
                  const inputBgClass = rowClassName ? "bg-transparent" : "";
                  const inputStyle = rowStyle.backgroundColor ? { backgroundColor: "transparent" } : undefined;

                  return (
                  <React.Fragment key={row.id || index}>
                  <tr
                    className={rowClassName}
                    style={rowStyle}
                    data-mobile-expanded={isMobileExpanded ? "true" : "false"}
                  >
                    {/* ✅ 복사 선택 체크박스 */}
                    <td className={`border border-gray-300 px-1.5 py-1 text-center print-hide hidden md:table-cell ${rowClassName}`} style={rowStyle}>
                      <input
                        type="checkbox"
                        checked={copySelectedIds.has(row.id)}
                        onChange={() => toggleCopySelection(row.id)}
                        aria-label={`${index + 1}번 예약 복사 선택`}
                      />
                    </td>
                    <td
                      className={`dispatch-mobile-summary border border-gray-300 ${rowClassName}`}
                      style={rowStyle}
                      colSpan={dispatchTableHeaders.length}
                    >
                      <div className="space-y-2">
                        <div>
                          <div className="mb-1 text-xs font-semibold text-gray-600">사용자/노선명</div>
                          <div className="relative w-full">
                            <Input
                              value={row.user || ""}
                              onChange={(event) => handleChange(index, "user", event.target.value)}
                              aria-label={`${index + 1}번 사용자/노선명`}
                              className={`w-full px-2 py-1 pr-5 ${inputBgClass}`}
                              style={inputStyle}
                            />
                            <button
                              type="button"
                              aria-label={`${index + 1}번 예약 메모`}
                              title={(row.memo || "").trim() ? row.memo : "메모 입력"}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                openReservationMemoModal(index, row);
                              }}
                              className="absolute right-[3px] top-[3px] h-3.5 w-3.5"
                            >
                              <span
                                className={`block h-0 w-0 border-l-[11px] border-l-transparent border-t-[11px] ${
                                  (row.memo || "").trim() ? "border-t-red-600" : "border-t-yellow-400"
                                }`}
                              />
                            </button>
                          </div>
                        </div>

                        {!isTentativeMode && (
                          <div className="grid grid-cols-2 gap-2">
                            <label className="min-w-0 text-xs font-semibold text-gray-600">
                              차량번호
                              <div
                                className="relative mt-1"
                                data-car-autocomplete-root="true"
                                onBlur={(event) => {
                                  if (!event.currentTarget.contains(event.relatedTarget)) {
                                    window.setTimeout(closeCarAutocomplete, 0);
                                  }
                                }}
                              >
                                <Input
                                  value={row.car_number || ""}
                                  onChange={(event) => handleChange(index, "car_number", event.target.value)}
                                  aria-label={`${index + 1}번 차량번호`}
                                  className={`w-full px-2 py-1 ${inputBgClass}`}
                                  style={inputStyle}
                                />
                                {searchingIndex === index && carSearchList.length > 0 && (
                                  <ul className="absolute z-30 max-h-40 w-full overflow-auto border bg-white text-xs text-gray-900">
                                    {carSearchList.map((car, suggestionIndex) => (
                                      <li
                                        key={suggestionIndex}
                                        className="cursor-pointer p-1 hover:bg-gray-200"
                                        onMouseDown={(event) => {
                                          event.preventDefault();
                                          selectCarSuggestion(index, car);
                                        }}
                                      >
                                        {car.car_number_only} {car.driver ? `· ${car.driver}` : ""}
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </label>
                            <label className="min-w-0 text-xs font-semibold text-gray-600">
                              기사명
                              <Input
                                value={row.human_name || ""}
                                onChange={(event) => handleChange(index, "human_name", event.target.value)}
                                onBlur={() => validateDriverHolidayCell(index)}
                                aria-label={`${index + 1}번 기사명`}
                                className={`mt-1 w-full px-2 py-1 ${inputBgClass}`}
                                style={inputStyle}
                              />
                            </label>
                          </div>
                        )}

                        <button
                          type="button"
                          className="flex w-full items-center justify-center py-1 text-base text-gray-700"
                          aria-label={`${index + 1}번 배차 상세 ${isMobileExpanded ? "접기" : "펼치기"}`}
                          aria-expanded={isMobileExpanded}
                          onClick={() => {
                            setMobileExpandedRows((current) => {
                              const next = new Set(current);
                              if (next.has(mobileRowKey)) next.delete(mobileRowKey);
                              else next.add(mobileRowKey);
                              return next;
                            });
                          }}
                        >
                          {isMobileExpanded ? "▲" : "▼"}
                        </button>
                      </div>
                    </td>
                    {dispatchTableHeaders.map((header) => {
                      const keyMap = {
                        "순번": (<button onClick={() => navigate(`/dispatch/general/${row.id}`, { state: row })} className="text-blue-600 underline">{index + 1}</button>),
                        "구분": row.reservation_type,
                        "운행일": row.date,
                        "사용자/노선명": (
                          <div className="relative w-full min-w-[191px] max-w-[251px]">
                            <Input
                              value={row.user || ""}
                              onChange={(e) => handleChange(index, "user", e.target.value)}
                              className={`w-full px-2 py-0.5 pr-4 ${inputBgClass}`}
                              style={inputStyle}
                            />
                            <button
                              type="button"
                              aria-label={`${index + 1}번 예약 메모`}
                              title={(row.memo || "").trim() ? row.memo : "메모 입력"}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                openReservationMemoModal(index, row);
                              }}
                              className="absolute right-[3px] top-[3px] h-3.5 w-3.5"
                            >
                              <span
                                className={`block h-0 w-0 border-l-[11px] border-l-transparent border-t-[11px] ${
                                  (row.memo || "").trim() ? "border-t-red-600" : "border-t-yellow-400"
                                }`}
                              />
                            </button>
                          </div>
                        ),
                        "편도/왕복": <span className="inline-block w-[42px] truncate text-center">{row.one_round}</span>,
                        "시간": (
                          <TimeSelect
                            value={row.time || ""}
                            onChange={(value) => handleChange(index, "time", value)}
                            className="min-w-[112px]"
                            compact
                            showClear={false}
                          />
                        ),
                        "출발시간": (
                          <TimeSelect
                            value={row.time || ""}
                            onChange={(value) => handleChange(index, "time", value)}
                            className="min-w-[112px]"
                            compact
                            showClear={false}
                          />
                        ),
                        "도착시간": (
                          <TimeSelect
                            value={row.arrival_time || ""}
                            onChange={(value) => handleChange(index, "arrival_time", value)}
                            className="min-w-[112px]"
                            compact
                            showClear={false}
                          />
                        ),
                        "회차시간": (
                          <TimeSelect
                            value={row.round_trip_time || ""}
                            onChange={(value) => handleChange(index, "round_trip_time", value)}
                            className="min-w-[112px]"
                            compact
                            showClear={false}
                          />
                        ),
                        "출발지": <Input value={row.start || ""} onChange={(e) => handleChange(index, "start", e.target.value)} className={`w-full min-w-[130px] max-w-[240px] px-2 py-0.5 ${inputBgClass}`} style={inputStyle} />,
                        "경유지": <Input value={row.transit || ""} onChange={(e) => handleChange(index, "transit", e.target.value)} className={`w-full min-w-[130px] max-w-[240px] px-2 py-0.5 ${inputBgClass}`} style={inputStyle} />,
                        "행선지": <Input value={row.destination || ""} onChange={(e) => handleChange(index, "destination", e.target.value)} className={`w-full min-w-[140px] max-w-[260px] px-2 py-0.5 ${inputBgClass}`} style={inputStyle} />,
                        "받을금액": <Input value={row.money || ""} onChange={(e) => handleChange(index, "money", e.target.value)} className={`w-full min-w-[60px] max-w-[80px] px-2 py-0.5 ${inputBgClass}`} style={inputStyle} />,
                        "차량번호": (
                                    <div
                                      className="relative"
                                      data-car-autocomplete-root="true"
                                      onBlur={(event) => {
                                        if (!event.currentTarget.contains(event.relatedTarget)) {
                                          window.setTimeout(closeCarAutocomplete, 0);
                                        }
                                      }}
                                    >
                                      <Input
                                        value={row.car_number || ""}
                                        onChange={(e) => handleChange(index, "car_number", e.target.value)}
                                        className={`w-full min-w-[50px] max-w-[120px] px-2 py-0.5 ${inputBgClass}`}
                                        style={inputStyle}
                                      />
                                      {searchingIndex === index && carSearchList.length > 0 && (
                                        <ul className="absolute z-10 bg-white border w-full max-h-40 overflow-auto max-w-[200px] text-xs">
                                          {carSearchList.map((car, i) => (
                                            <li
                                              key={i}
                                              className="p-1 hover:bg-gray-200 cursor-pointer text-xs"
                                              onMouseDown={(e) => {
                                                e.preventDefault();
                                                selectCarSuggestion(index, car);
                                              }}
                                            >
                                              {car.car_number_only} {car.driver ? `· ${car.driver}` : ""}
                                            </li>
                                          ))}
                                        </ul>
                                      )}
                                    </div>
                                  ),
                        "기사명": (
                          <Input
                            value={row.human_name || ""}
                            onChange={(e) => handleChange(index, "human_name", e.target.value)}
                            onBlur={() => validateDriverHolidayCell(index)}
                            className={`w-full min-w-[57px] max-w-[95px] px-2 py-0.5 ${inputBgClass}`}
                            style={inputStyle}
                          />
                        ),
                        "기사연락처": <Input value={row.phone || ""} onChange={(e) => handleChange(index, "phone", e.target.value)} className={`w-full min-w-[50px] max-w-[110px] px-2 py-0.5 ${inputBgClass}`} style={inputStyle} />,
                      };
                      const isDriverCheckedTypeCell = header === "구분" && row.driver_check === true;
                      const isWeekendOnlyDateCell =
                        header === "운행일" && isWeekendDate(row.date) && !isBusInspectionRow;
                      const cellStyle =
                        isDriverCheckedTypeCell
                          ? { backgroundColor: "#4b5563", color: "#ffffff", fontWeight: 700 }
                          : isWeekendOnlyDateCell
                          ? undefined
                          : rowStyle;
                      return (
                        <td
                          key={header}
                          className={`border border-gray-300 px-1.5 py-1 ${
                            isDriverCheckedTypeCell
                              ? ""
                              : isWeekendOnlyDateCell
                              ? "bg-green-200"
                              : rowClassName
                          } ${hiddenOnMobile(header)} ${isPrintHiddenHeader(header) ? "print-hide" : ""}`}
                          data-label={header}
                          style={cellStyle}
                        >
                          {keyMap[header]}
                        </td>
                      );
                    })}
                  </tr>
                  {(row.user_phone || "").trim() && (
                    <tr className={`dispatch-phone-row ${rowClassName}`} style={rowStyle}>
                      <td className={`border border-gray-300 px-1.5 py-1 print-hide ${rowClassName}`} style={rowStyle} />
                      <td className={`border border-gray-300 px-1.5 py-1 ${rowClassName}`} style={rowStyle} colSpan={3} />
                      <td className={`border border-gray-300 px-1.5 py-1 ${rowClassName}`} style={rowStyle}>
                        <Input
                          value={row.user_phone || ""}
                          onChange={(e) => handleChange(index, "user_phone", e.target.value)}
                          placeholder="사용자 전화번호"
                          aria-label={`${index + 1}번 사용자 전화번호`}
                          className={`w-full min-w-[191px] max-w-[251px] px-2 py-0.5 ${inputBgClass}`}
                          style={inputStyle}
                        />
                      </td>
                      <td className={`border border-gray-300 px-1.5 py-1 ${rowClassName}`} style={rowStyle} colSpan={dispatchTableHeaders.length - 4} />
                    </tr>
                  )}
                  </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      <div className="text-right no-print">
        <Button className="hidden md:inline-flex mt-4 mr-2" onClick={openUnassignedStatus} disabled={unassignedLoading}>
          {unassignedLoading ? "조회 중..." : "미배차현황"}
        </Button>
        <Button className="hidden md:inline-flex mt-4 mr-2" onClick={openDispatchRuleModal} disabled={dispatchRuleLoading || saving || uiLocked}>
          {dispatchRuleLoading ? "조회 중..." : "배차규칙 적용"}
        </Button>
        <Button className="hidden md:inline-flex mt-4 mr-2" variant="outline" onClick={handleDownloadCsv} disabled={filteredData.length === 0}>
          엑셀시트 받기
        </Button>
        <Button className="mt-4" onClick={handleUpdate} 
           disabled={saving || uiLocked}
        >
          {saving ? "저장중..." : "수정"}
        </Button>
        <Button className="hidden md:inline-flex mt-4 ml-2" variant="outline" onClick={handlePrint}>
          인쇄
        </Button>
        <Button
          className={`hidden md:inline-flex mt-4 ml-2 ${
            copySelectedIds.size === 0 || copying || deleting
              ? "bg-gray-400 hover:bg-gray-400 cursor-not-allowed"
              : ""
          }`}
          onClick={openCopyModal}
          disabled={copySelectedIds.size === 0 || copying || deleting}
        >
          복사
        </Button>
        <Button
          className={`hidden md:inline-flex mt-4 ml-2 ${
            copySelectedIds.size === 0 || deleting
              ? "bg-gray-400 hover:bg-gray-400 cursor-not-allowed"
              : "bg-red-600 hover:bg-red-700"
          }`}
          onClick={handleBulkDelete}
          disabled={copySelectedIds.size === 0 || deleting}
        >
          {deleting ? "삭제 중..." : "삭제"}
        </Button>
        {isTentativeMode && (
          <Button
            className={`hidden md:inline-flex mt-4 ml-2 ${
              copySelectedIds.size === 0 || confirmingTentative
                ? "bg-gray-400 hover:bg-gray-400 cursor-not-allowed"
                : "bg-emerald-600 hover:bg-emerald-700"
            }`}
            onClick={handleConfirmTentativeReservations}
            disabled={copySelectedIds.size === 0 || confirmingTentative}
          >
            {confirmingTentative ? "확정 중..." : "예약 확정"}
          </Button>
        )}
      </div>

      {copyModalOpen && (
        <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 px-4 no-print">
          <div className="w-full max-w-md rounded bg-white p-5 shadow-lg">
            <p className="mb-4 font-medium">
              {copySelectedIds.size}개를 어느 날짜로 복사하시겠습니까?
            </p>
            <div className="mb-4 flex gap-4 text-sm">
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="copyDateMode"
                  value="single"
                  checked={copyDateMode === "single"}
                  onChange={() => setCopyDateMode("single")}
                  disabled={copying}
                />
                하나의 날짜
              </label>
              <label className="flex items-center gap-2">
                <input
                  type="radio"
                  name="copyDateMode"
                  value="range"
                  checked={copyDateMode === "range"}
                  onChange={() => setCopyDateMode("range")}
                  disabled={copying}
                />
                여러개의 날짜
              </label>
            </div>

            {copyDateMode === "single" ? (
              <Input
                type="date"
                value={copyTargetDate}
                onChange={(e) => setCopyTargetDate(e.target.value)}
                className="mb-5 w-full"
              />
            ) : (
              <div className="mb-5 space-y-3">
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={copyRangeStartDate}
                    onChange={(e) => setCopyRangeStartDate(e.target.value)}
                    className="w-full"
                  />
                  <span className="text-sm text-gray-600">~</span>
                  <Input
                    type="date"
                    value={copyRangeEndDate}
                    onChange={(e) => setCopyRangeEndDate(e.target.value)}
                    className="w-full"
                  />
                </div>
                <div className="flex gap-4 text-sm">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={copyDateFilter === "excludeWeekend"}
                      onChange={() =>
                        setCopyDateFilter((prev) => prev === "excludeWeekend" ? "all" : "excludeWeekend")
                      }
                      disabled={copying}
                    />
                    주말 제외
                  </label>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={copyDateFilter === "weekendOnly"}
                      onChange={() =>
                        setCopyDateFilter((prev) => prev === "weekendOnly" ? "all" : "weekendOnly")
                      }
                      disabled={copying}
                    />
                    주말만 넣기
                  </label>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2">
              <button
                type="button"
                onClick={closeCopyModal}
                disabled={copying}
                className="rounded border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                취소
              </button>
              <Button type="button" onClick={handleBulkCopy} disabled={copying}>
                {copying ? "복사 중..." : "복사"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {reservationMemoModal && (
        <div className="fixed inset-0 z-[240] flex items-center justify-center bg-black/40 px-4 no-print">
          <div className="w-full max-w-lg rounded bg-white p-5 shadow-lg">
            <div className="mb-3">
              <h3 className="text-lg font-semibold">예약 메모</h3>
              <p className="mt-1 text-sm text-gray-600">
                {reservationMemoModal.user || "사용자/노선명 없음"}
              </p>
            </div>
            <textarea
              value={reservationMemoModal.memo}
              onChange={(event) =>
                setReservationMemoModal((prev) =>
                  prev ? { ...prev, memo: event.target.value } : prev
                )
              }
              placeholder="이 예약에 대한 메모를 입력하세요."
              className="h-40 w-full resize-y rounded border border-gray-300 px-3 py-2 text-sm outline-none focus:border-blue-500"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={closeReservationMemoModal}
                disabled={reservationMemoSaving}
                className="rounded border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-100 disabled:cursor-not-allowed disabled:opacity-60"
              >
                취소
              </button>
              <Button type="button" onClick={saveReservationMemo} disabled={reservationMemoSaving}>
                {reservationMemoSaving ? "저장 중..." : "저장"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {dispatchRuleOpen && (
        <div className="fixed inset-0 z-[220] flex items-center justify-center bg-black/40 px-4 no-print">
          <div className="flex max-h-[88vh] w-full max-w-6xl flex-col rounded bg-white p-5 shadow-lg">
            <div className="mb-3 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">배차규칙 적용</h3>
                <p className="text-sm text-gray-600">
                  저장된 규칙을 선택한 뒤 미리보기를 만들고, 선택한 항목만 실제 배차에 반영합니다.
                </p>
              </div>
              <button
                type="button"
                onClick={closeDispatchRuleModal}
                className="rounded border px-3 py-1 text-sm hover:bg-gray-100"
              >
                닫기
              </button>
            </div>

            <div className="grid min-h-0 flex-1 grid-cols-1 gap-4 lg:grid-cols-[0.85fr_1.4fr]">
              <section className="min-h-0 overflow-auto rounded border">
                <div className="sticky top-0 bg-gray-50 px-3 py-2 font-semibold">
                  적용할 규칙
                </div>
                {dispatchRules.length === 0 ? (
                  <div className="p-5 text-center text-sm text-gray-500">
                    저장된 배차 규칙이 없습니다.
                  </div>
                ) : (
                  <div className="divide-y">
                    {dispatchRules.map((rule) => {
                      const ruleDrivers = getDispatchRuleDrivers(rule);
                      return (
                      <div
                        key={rule.id}
                        className={`flex items-start gap-2 px-3 py-2 text-sm hover:bg-gray-50 ${
                          rule.enabled === false ? "text-gray-400" : ""
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={selectedDispatchRuleIds.has(rule.id)}
                          onChange={() => toggleDispatchRule(rule.id)}
                          disabled={rule.enabled === false}
                          className="mt-1"
                        />
                        <div className="min-w-0 flex-1">
                          <span className="block font-semibold">{rule.name}</span>
                          <span className="block text-gray-600">
                            {formatDispatchRuleType(rule.rule_type)} · {formatDispatchRuleRoutes(rule)}
                            {rule.enabled === false ? " · 중지" : ""}
                          </span>
                          {rule.rule_type === "rotation" && (
                            <label className="mt-2 block text-xs text-gray-700">
                              <span className="mb-1 block font-semibold">이번 시작 기사</span>
                              <select
                                className="h-8 w-full rounded border px-2 text-sm disabled:bg-gray-100"
                                value={dispatchRuleStartDriverIds[rule.id] || ""}
                                onChange={(e) => updateDispatchRuleStartDriver(rule.id, e.target.value)}
                                disabled={rule.enabled === false || !selectedDispatchRuleIds.has(rule.id)}
                              >
                                {ruleDrivers.map((driver) => (
                                  <option key={driver.id} value={driver.driver_profile_id || driver.driver_name || ""}>
                                    {driver.driver_name}
                                  </option>
                                ))}
                              </select>
                            </label>
                          )}
                        </div>
                      </div>
                    );
                    })}
                  </div>
                )}
              </section>

              <section className="min-h-0 overflow-auto rounded border">
                <div className="sticky top-0 z-10 flex items-center justify-between bg-gray-50 px-3 py-2">
                  <div className="font-semibold">
                    추천 미리보기 ({dispatchRulePreview.length}건)
                  </div>
                  <div className="flex gap-2">
                    <Button type="button" onClick={() => buildDispatchRulePreview()} disabled={dispatchRuleLoading || dispatchRules.length === 0}>
                      미리보기
                    </Button>
                    <Button type="button" onClick={() => buildDispatchRulePreview({ randomize: true })} disabled={dispatchRuleLoading || dispatchRules.length === 0}>
                      다시 추천
                    </Button>
                    <Button type="button" onClick={applyDispatchRulePreview} disabled={dispatchRuleApplying || selectedDispatchPreviewIds.size === 0}>
                      {dispatchRuleApplying ? "적용 중..." : "선택 적용"}
                    </Button>
                  </div>
                </div>
                <table className="w-full min-w-[900px] border-collapse text-sm">
                  <thead className="bg-blue-500 text-white">
                    <tr>
                      <th className="border p-2">
                        <input
                          type="checkbox"
                          checked={dispatchRulePreview.length > 0 && dispatchRulePreview.every((row) => selectedDispatchPreviewIds.has(row.previewId))}
                          onChange={toggleAllDispatchPreview}
                        />
                      </th>
                      <th className="border p-2">처리</th>
                      <th className="border p-2">운행일</th>
                      <th className="border p-2">사용자/노선명</th>
                      <th className="border p-2">현재 기사</th>
                      <th className="border p-2">추천 기사</th>
                      <th className="border p-2">추천 차량</th>
                      <th className="border p-2">적용 규칙</th>
                      <th className="border p-2">이유</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dispatchRulePreview.length === 0 ? (
                      <tr>
                        <td colSpan={9} className="border p-6 text-center text-gray-500">
                          미리보기를 생성해 주세요.
                        </td>
                      </tr>
                    ) : (
                      dispatchRulePreview.map((row) => (
                        <tr key={row.previewId} className={row.actionType === "clear" ? "bg-amber-50 hover:bg-amber-100" : "hover:bg-gray-50"}>
                          <td className="border p-2 text-center">
                            <input
                              type="checkbox"
                              checked={selectedDispatchPreviewIds.has(row.previewId)}
                              onChange={() => toggleDispatchPreview(row.previewId)}
                            />
                          </td>
                          <td className="border p-2 text-center">{row.actionType === "clear" ? "기존 배차 해제" : "배정"}</td>
                          <td className="border p-2 text-center">{row.date}</td>
                          <td className="border p-2">{row.routeUser}</td>
                          <td className="border p-2 text-center">{row.currentDriverName || "-"}</td>
                          <td className="border p-2 text-center">{row.actionType === "clear" ? "비움" : row.recommendedDriverName || "-"}</td>
                          <td className="border p-2 text-center">{row.actionType === "clear" ? "비움" : row.recommendedCarNumber || "-"}</td>
                          <td className="border p-2">{row.ruleName}</td>
                          <td className="border p-2">{row.reason}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </section>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

function hiddenOnMobile(label) {
  const visible = ["운행일", "사용자/노선명", "차량번호", "기사명", "시간", "출발시간", "출발지", "경유지", "행선지"];
  if (!visible.includes(label)) return "hidden md:table-cell";
  const primary = ["사용자/노선명", "차량번호", "기사명"];
  return primary.includes(label)
    ? "mobile-dispatch-cell mobile-dispatch-primary-cell"
    : "mobile-dispatch-cell";
}

const DISPATCH_RULE_TYPE_LABELS = {
  weekday_fixed: "요일별 고정",
  rotation: "순번제",
  balanced: "균등 배정",
};

const DISPATCH_WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];

function formatDispatchRuleType(value) {
  return DISPATCH_RULE_TYPE_LABELS[value] || value || "";
}

function getDispatchRuleRoutes(rule = {}) {
  const routeRows = [...(rule.dispatch_rule_routes || [])]
    .sort((a, b) => (a.order_no || 0) - (b.order_no || 0))
    .map((row) => ({
      routeUser: String(row.route_user || "").trim(),
      routeTime: normalizeRuleTime(row.route_time),
      routeKey: makeDispatchRouteKey(row.route_user, row.route_time),
    }))
    .filter((row) => row.routeUser);
  if (routeRows.length > 0) return routeRows;
  return String(rule.route_pattern || "").trim()
    ? [{ routeUser: String(rule.route_pattern || "").trim(), routeTime: "", routeKey: makeDispatchRouteKey(rule.route_pattern, "") }]
    : [];
}

function getDispatchRuleDrivers(rule = {}) {
  return [...(rule.dispatch_rule_drivers || [])].sort((a, b) => (a.order_no || 0) - (b.order_no || 0));
}

function formatDispatchRuleRoutes(rule = {}) {
  const routes = getDispatchRuleRoutes(rule);
  if (routes.length === 0) return "노선 없음";
  const labels = routes.map(formatDispatchRouteRuleLabel);
  if (labels.length === 1) return labels[0];
  return `${labels[0]} 외 ${labels.length - 1}개`;
}

function normalizeRuleTime(value = "") {
  const match = String(value || "").match(/^(\d{1,2}):(\d{2})/);
  if (!match) return "";
  return `${match[1].padStart(2, "0")}:${match[2]}`;
}

function makeDispatchRouteKey(route = "", time = "") {
  const routeUser = String(route || "").trim();
  const routeTime = normalizeRuleTime(time);
  return routeTime ? `${routeUser}|||${routeTime}` : routeUser;
}

function formatDispatchRouteRuleLabel(route = {}) {
  return route.routeTime ? `${route.routeUser} · ${route.routeTime}` : route.routeUser;
}

function isDispatchRouteMatch(route = {}, row = {}) {
  if (route.routeUser !== String(row.user || "").trim()) return false;
  if (!route.routeTime) return true;
  return route.routeTime === normalizeRuleTime(row.time);
}

function getRuleWeekdayKey(dateValue = "") {
  const date = new Date(`${dateValue}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  return DISPATCH_WEEKDAY_KEYS[date.getDay()];
}

function hydrateRuleDriver(driverRow = {}, driverIndex = new Map()) {
  const name = String(driverRow.driver_name || "").trim();
  const driver = driverIndex.get(name);
  if (driver?.duplicate) {
    return {
      name,
      carNumber: driverRow.car_number || "",
      phone: "",
      email: "",
    };
  }
  return {
    name,
    carNumber: getCarNumberMatchKey(driverRow.car_number || driver?.car_number || ""),
    phone: driver?.phone || "",
    email: driver?.email || "",
  };
}

function getRouteAllowedDrivers(rule = {}, route = {}, drivers = []) {
  const permissions = rule.options_json?.driver_route_permissions || {};
  return drivers.filter((driver) => {
    const driverId = driver.driver_profile_id;
    const allowedRoutes = driverId ? permissions[driverId] : null;
    if (!Array.isArray(allowedRoutes)) return true;
    return allowedRoutes.includes(route.routeKey);
  });
}

function hasDriverRoutePermissions(rule = {}) {
  const permissions = rule.options_json?.driver_route_permissions || {};
  return Object.values(permissions).some((routeKeys) => Array.isArray(routeKeys));
}

function getDriverCandidateKey(driver = {}) {
  return driver.driver_profile_id || normalizeDriverName(driver.driver_name);
}

function hasPreviewDriverTimeConflict(driver = {}, row = {}, previewDriverReservations = new Map()) {
  const driverKey = getDriverCandidateKey(driver);
  if (!driverKey || !row.date) return false;

  const reservations = previewDriverReservations.get(driverKey) || [];
  return reservations.some((reservation) =>
    reservation.date === row.date && hasReservationTimeConflict(row, reservation)
  );
}

function hasPreviewDriverDateConflict(driver = {}, row = {}, previewDriverReservations = new Map()) {
  const driverKey = getDriverCandidateKey(driver);
  if (!driverKey || !row.date) return false;

  const reservations = previewDriverReservations.get(driverKey) || [];
  return reservations.some((reservation) => reservation.date === row.date);
}

function pickDriverForRule({ rule, row, route, routeIndex, dateIndexByValue, assignedCount, driverIndex, randomizeBalanced = false, rotationStartDriverIds = {}, previewDriverReservations = new Map() }) {
  const drivers = getRouteAllowedDrivers(rule, route, getDispatchRuleDrivers(rule));
  if (drivers.length === 0) return null;
  const availableDrivers = drivers.filter((driver) => {
    if (hasPreviewDriverTimeConflict(driver, row, previewDriverReservations)) return false;
    if (rule.rule_type === "rotation" && hasPreviewDriverDateConflict(driver, row, previewDriverReservations)) return false;
    return true;
  });
  if (availableDrivers.length === 0) {
    return {
      driverRow: null,
      reason: rule.rule_type === "rotation"
        ? "같은 날짜에 추천 가능한 후보 기사가 없습니다."
        : "같은 시간대에 추천 가능한 후보 기사가 없습니다.",
    };
  }

  if (rule.rule_type === "weekday_fixed") {
    const weekday = getRuleWeekdayKey(row.date);
    const driverRow = availableDrivers.find((driver) => driver.weekday === weekday);
    return driverRow
      ? { driverRow, reason: `${formatDispatchRuleType(rule.rule_type)} · ${weekdayLabel(weekday)} 담당` }
      : { driverRow: null, reason: `${formatDispatchRuleType(rule.rule_type)} · ${weekdayLabel(weekday)} 담당 기사가 같은 시간대에 이미 추천되었습니다.` };
  }

  if (rule.rule_type === "rotation") {
    const uniqueDateIndex = dateIndexByValue.get(row.date) || 0;
    const startIndex = getRuleRotationStartIndex(rule, drivers, rotationStartDriverIds[rule.id]);
    const orderedDrivers = rotateDriversByStart(drivers, startIndex);
    const shiftedDrivers = rotateDriversByStart(orderedDrivers, (routeIndex + uniqueDateIndex) % drivers.length);
    const driverRow = shiftedDrivers.find((driver) => availableDrivers.some((available) => available.id === driver.id));
    const driverIndexValue = drivers.findIndex((driver) => driver.id === driverRow?.id);
    return {
      driverRow,
      reason: driverIndexValue >= 0
        ? `순번제 · 이번 시작 ${drivers[startIndex]?.driver_name || `${startIndex + 1}번째`} · 노선 ${routeIndex + 1}번째, 조회일 ${uniqueDateIndex + 1}번째 순환${driverIndexValue !== ((startIndex + routeIndex + uniqueDateIndex) % drivers.length) ? " · 중복 후보 제외" : ""}`
        : "순번제 · 같은 날짜에 추천 가능한 후보 기사가 없습니다.",
    };
  }

  if (rule.rule_type === "balanced") {
    const sorted = [...availableDrivers].sort((a, b) => {
      const aCount = assignedCount.get(a.driver_profile_id || a.driver_name) || 0;
      const bCount = assignedCount.get(b.driver_profile_id || b.driver_name) || 0;
      if (aCount !== bCount) return aCount - bCount;
      return (a.order_no || 0) - (b.order_no || 0);
    });
    const minCount = assignedCount.get(sorted[0].driver_profile_id || sorted[0].driver_name) || 0;
    const leastAssignedDrivers = sorted.filter((driver) => (assignedCount.get(driver.driver_profile_id || driver.driver_name) || 0) === minCount);
    const selectedDriver = randomizeBalanced && leastAssignedDrivers.length > 1
      ? leastAssignedDrivers[Math.floor(Math.random() * leastAssignedDrivers.length)]
      : sorted[0];
    return {
      driverRow: selectedDriver,
      reason: randomizeBalanced && leastAssignedDrivers.length > 1
        ? "균등 배정 · 최저 배정 후보 중 다시 추천"
        : "균등 배정 · 현재 추천 횟수가 가장 적은 기사",
    };
  }

  return null;
}

function rotateDriversByStart(drivers = [], startIndex = 0) {
  if (drivers.length === 0) return [];
  const normalizedStart = ((startIndex % drivers.length) + drivers.length) % drivers.length;
  return [...drivers.slice(normalizedStart), ...drivers.slice(0, normalizedStart)];
}

function getRuleRotationStartIndex(rule = {}, drivers = [], overrideDriverId = "") {
  if (overrideDriverId) {
    const overrideIndex = drivers.findIndex((driver) =>
      driver.driver_profile_id === overrideDriverId || driver.driver_name === overrideDriverId
    );
    if (overrideIndex >= 0) return overrideIndex;
  }

  const options = rule.options_json || {};
  const savedIndex = Number(options.rotation_start_index);
  if (Number.isInteger(savedIndex) && savedIndex >= 0 && savedIndex < drivers.length) return savedIndex;

  const savedDriverId = options.rotation_start_driver_id;
  if (savedDriverId) {
    const driverIndex = drivers.findIndex((driver) => driver.driver_profile_id === savedDriverId);
    if (driverIndex >= 0) return driverIndex;
  }

  return 0;
}

function getRuleBalanceGroupKey(rule = {}) {
  const group = String(rule.options_json?.rule_group || "").trim();
  return group || rule.id || "";
}

async function fetchDispatchRuleWeightHistory(rows = [], rules = []) {
  const company = localStorage.getItem("email");
  const hasBalancedRules = rules.some((rule) => rule.rule_type === "balanced");
  const targetDates = rows.map((row) => row.date).filter(Boolean);
  if (!company || !hasBalancedRules || targetDates.length === 0) return [];

  const minTargetDate = targetDates.reduce((min, date) => (date < min ? date : min), targetDates[0]);
  const maxTargetDate = targetDates.reduce((max, date) => (date > max ? date : max), targetDates[0]);
  const historyFrom = addDateDays(minTargetDate, -7);
  const historyTo = addDateDays(maxTargetDate, -1);
  if (!historyFrom || !historyTo) return [];

  const historyRows = [];
  const pageSize = 1000;
  for (let from = 0; ; from += pageSize) {
    const to = from + pageSize - 1;
    const { data, error } = await supabase
      .from("reservations")
      .select("id, date, user, time, human_name")
      .eq("company", company)
      .gte("date", historyFrom)
      .lte("date", historyTo)
      .range(from, to);

    if (error) throw error;
    historyRows.push(...(data || []));
    if (!data || data.length < pageSize) break;
  }

  return historyRows;
}

function createRollingWeightInitialCounts(targetRows = [], rules = [], historyRows = []) {
  const initialCounts = new Map();
  const counted = new Set();
  const targetDates = Array.from(new Set(targetRows.map((row) => row.date).filter(Boolean)));
  const balancedRules = rules.filter((rule) => rule.rule_type === "balanced");
  if (targetDates.length === 0 || balancedRules.length === 0 || historyRows.length === 0) return initialCounts;

  targetDates.forEach((targetDate) => {
    const fromDate = addDateDays(targetDate, -7);
    const toDate = addDateDays(targetDate, -1);
    if (!fromDate || !toDate) return;

    balancedRules.forEach((rule) => {
      const balanceGroupKey = getRuleBalanceGroupKey(rule);
      const countKeyByRule = `${balanceGroupKey}:${targetDate}`;
      const countMap = initialCounts.get(countKeyByRule) || new Map();
      const driverKeyByName = getRuleDriverKeyByName(rule);

      historyRows.forEach((row) => {
        if (!row.date || row.date < fromDate || row.date > toDate) return;
        if (!isRuleWeekdayIncluded(rule, row.date)) return;
        if (!getDispatchRuleRoutes(rule).some((route) => isDispatchRouteMatch(route, row))) return;

        const driverName = normalizeDriverName(row.human_name);
        if (!driverName) return;
        const driverKey = driverKeyByName.get(driverName);
        if (!driverKey) return;

        const countedKey = `${countKeyByRule}:${row.date}:${driverKey}`;
        if (counted.has(countedKey)) return;
        counted.add(countedKey);
        countMap.set(driverKey, (countMap.get(driverKey) || 0) + 1);
      });

      if (countMap.size > 0) initialCounts.set(countKeyByRule, countMap);
    });
  });

  return initialCounts;
}

function getRuleDriverKeyByName(rule = {}) {
  const map = new Map();
  getDispatchRuleDrivers(rule).forEach((driver) => {
    const name = normalizeDriverName(driver.driver_name);
    if (!name) return;
    map.set(name, driver.driver_profile_id || driver.driver_name);
  });
  return map;
}

function isRuleWeekdayIncluded(rule = {}, dateValue = "") {
  const weekdays = Array.isArray(rule.weekdays) ? rule.weekdays : [];
  if (weekdays.length === 0) return true;
  const weekday = getRuleWeekdayKey(dateValue);
  return !!weekday && weekdays.includes(weekday);
}

function addDateDays(dateValue = "", days = 0) {
  const date = parseLocalDate(dateValue);
  if (!date) return "";
  date.setDate(date.getDate() + days);
  return formatLocalDate(date);
}

function parseLocalDate(dateValue = "") {
  const match = String(dateValue || "").match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return null;
  const date = new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]));
  return Number.isNaN(date.getTime()) ? null : date;
}

function formatLocalDate(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function formatDispatchPreviewReason({ rule = {}, pickedReason = "", isReusedBalancedPick = false, balanceGroupKey = "" }) {
  if (rule.rule_type !== "balanced") return pickedReason;

  const group = String(rule.options_json?.rule_group || "").trim();
  if (isReusedBalancedPick) {
    return group
      ? `균등 배정 · 그룹 '${group}' · 같은 날짜 선택 노선 묶음`
      : "균등 배정 · 같은 날짜 선택 노선 묶음";
  }

  if (group && String(pickedReason).includes("다시 추천")) {
    return `균등 배정 · 그룹 '${group}' · 최저 배정 후보 중 다시 추천`;
  }
  if (group) return `균등 배정 · 그룹 '${group}' · 직전 7일 적용요일 가중치가 가장 낮은 기사`;
  if (balanceGroupKey) return "균등 배정 · 직전 7일 적용요일 가중치가 가장 낮은 기사";
  return pickedReason;
}

function createDispatchRulePreview(rows = [], rules = [], driverIndex = new Map(), options = {}) {
  const randomizeBalanced = !!options.randomizeBalanced;
  const initialAssignedCounts = options.initialAssignedCounts || new Map();
  const rotationStartDriverIds = options.rotationStartDriverIds || {};
  const enabledRules = [...rules]
    .filter((rule) => rule.enabled !== false)
    .sort((a, b) => (Number(a.priority) || 10) - (Number(b.priority) || 10));
  const uniqueDates = Array.from(new Set(rows.map((row) => row.date).filter(Boolean))).sort();
  const dateIndexByValue = new Map(uniqueDates.map((date, index) => [date, index]));
  const assignedCountByRule = new Map(
    Array.from(initialAssignedCounts.entries()).map(([key, value]) => [key, new Map(value)])
  );
  const balancedDriverByRuleDate = new Map();
  const previewDriverReservations = new Map();
  const preview = [];

  rows.forEach((row, rowIndex) => {
    const routeUser = String(row.user || "").trim();
    if (!routeUser) return;

    for (const rule of enabledRules) {
      const routes = getDispatchRuleRoutes(rule);
      const routeIndex = routes.findIndex((route) => isDispatchRouteMatch(route, row));
      if (routeIndex < 0) continue;
      const matchedRoute = routes[routeIndex];

      if (Array.isArray(rule.weekdays) && rule.weekdays.length > 0) {
        const weekday = getRuleWeekdayKey(row.date);
        if (weekday && !rule.weekdays.includes(weekday)) continue;
      }

      const balanceGroupKey = rule.rule_type === "balanced" ? getRuleBalanceGroupKey(rule) : rule.id;
      const countKeyByRule = rule.rule_type === "balanced"
        ? `${balanceGroupKey}:${row.date || ""}`
        : rule.id;
      const countMap = assignedCountByRule.get(countKeyByRule) || new Map();
      const shouldReuseBalancedDriver = rule.rule_type === "balanced" && !hasDriverRoutePermissions(rule);
      const balancedDateKey = shouldReuseBalancedDriver ? `${rule.id}:${row.date || ""}` : "";
      const savedBalancedPickRaw = balancedDateKey ? balancedDriverByRuleDate.get(balancedDateKey) : null;
      const savedBalancedPick = savedBalancedPickRaw && !hasPreviewDriverTimeConflict(savedBalancedPickRaw.driverRow, row, previewDriverReservations)
        ? savedBalancedPickRaw
        : null;
      const picked = savedBalancedPick || pickDriverForRule({
        rule,
        row,
        route: matchedRoute,
        routeIndex,
        dateIndexByValue,
        assignedCount: countMap,
        driverIndex,
        randomizeBalanced,
        rotationStartDriverIds,
        previewDriverReservations,
      });
      if (!picked?.driverRow) continue;
      const isReusedBalancedPick = !!savedBalancedPick;
      if (balancedDateKey && !savedBalancedPick) {
        balancedDriverByRuleDate.set(balancedDateKey, picked);
      }

      const hydrated = hydrateRuleDriver(picked.driverRow, driverIndex);
      if (!hydrated.name) continue;

      const countKey = picked.driverRow.driver_profile_id || picked.driverRow.driver_name;
      if (!isReusedBalancedPick) {
        countMap.set(countKey, (countMap.get(countKey) || 0) + 1);
        assignedCountByRule.set(countKeyByRule, countMap);
      }

      preview.push({
        previewId: `${row.id || rowIndex}-${rule.id}`,
        reservationId: row.id,
        rowIndex,
        date: row.date,
        routeUser: formatDispatchRouteRuleLabel({ routeUser, routeTime: normalizeRuleTime(row.time) }),
        routeUserRaw: routeUser,
        routeTime: matchedRoute.routeTime || normalizeRuleTime(row.time),
        currentDriverName: row.human_name || "",
        currentCarNumber: row.car_number || "",
        recommendedDriverName: hydrated.name,
        recommendedDriverProfileId: picked.driverRow.driver_profile_id || null,
        recommendedCarNumber: hydrated.carNumber,
        recommendedPhone: hydrated.phone,
        recommendedEmail: hydrated.email,
        ruleId: rule.id,
        ruleName: rule.name || "",
        actionType: "assign",
        reason: formatDispatchPreviewReason({
          rule,
          pickedReason: picked.reason,
          isReusedBalancedPick,
          balanceGroupKey,
        }),
      });
      reservePreviewDriverTime(picked.driverRow, row, previewDriverReservations);
      break;
    }
  });

  return appendDriverDisplacementPreviews(rows, preview);
}

function reservePreviewDriverTime(driver = {}, row = {}, previewDriverReservations = new Map()) {
  const driverKey = getDriverCandidateKey(driver);
  if (!driverKey || !row.date) return;

  const reservations = previewDriverReservations.get(driverKey) || [];
  reservations.push({
    date: row.date,
    time: row.time || null,
    arrival_time: row.arrival_time || null,
  });
  previewDriverReservations.set(driverKey, reservations);
}

function appendDriverDisplacementPreviews(rows = [], previewRows = []) {
  const assignRows = previewRows.filter((row) => row.actionType !== "clear");
  const assignedTargetKeys = new Set(assignRows.map((row) => getPreviewTargetKey(row)));
  const clearKeys = new Set();
  const clearRows = [];

  assignRows.forEach((assignRow) => {
    const assignedDriver = normalizeDriverName(assignRow.recommendedDriverName);
    if (!assignedDriver || !assignRow.date) return;

    rows.forEach((row, rowIndex) => {
      if (!row) return;
      const rowKey = getPreviewTargetKey({ reservationId: row.id, rowIndex });
      if (rowKey === getPreviewTargetKey(assignRow)) return;
      if (assignedTargetKeys.has(rowKey)) return;
      if (clearKeys.has(rowKey)) return;
      if (row.date !== assignRow.date) return;
      if (normalizeDriverName(row.human_name) !== assignedDriver) return;

      clearKeys.add(rowKey);
      clearRows.push({
        previewId: `${row.id || rowIndex}-clear-${assignRow.previewId}`,
        reservationId: row.id,
        rowIndex,
        date: row.date,
        routeUser: formatDispatchRouteRuleLabel({
          routeUser: String(row.user || "").trim(),
          routeTime: normalizeRuleTime(row.time),
        }),
        routeUserRaw: String(row.user || "").trim(),
        routeTime: normalizeRuleTime(row.time),
        currentDriverName: row.human_name || "",
        currentCarNumber: row.car_number || "",
        recommendedDriverName: "",
        recommendedCarNumber: "",
        recommendedPhone: "",
        recommendedEmail: "",
        ruleId: assignRow.ruleId,
        ruleName: assignRow.ruleName || "",
        actionType: "clear",
        reason: `${assignRow.recommendedDriverName} 기사가 '${assignRow.routeUser}'에 배정되어 같은 날짜의 기존 노선을 비웁니다.`,
      });
    });
  });

  return [...previewRows, ...clearRows];
}

function getPreviewTargetKey(row = {}) {
  return row.reservationId || `row:${row.rowIndex}`;
}

function weekdayLabel(value = "") {
  return {
    mon: "월요일",
    tue: "화요일",
    wed: "수요일",
    thu: "목요일",
    fri: "금요일",
    sat: "토요일",
    sun: "일요일",
  }[value] || value;
}

function isPrintHiddenHeader(label) {
  // 인쇄에서 숨길 열들
  return ["복사", "순번", "기사연락처"].includes(label);
}

function getConflictRowStyle(level) {
  if (level === "external") return { backgroundColor: "#bfdbfe" };
  if (level === "overlap") return { backgroundColor: "#f87171" };
  if (level === "inspection") return { backgroundColor: "#fdba74" };
  if (level === "same-day") return { backgroundColor: "#f9a8d4" };
  return {};
}

function createGroupId() {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `copy-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function isWeekendDate(value = "") {
  if (!value) return false;
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return false;
  const day = date.getDay();
  return day === 0 || day === 6;
}

function writeUnassignedStatusPopup(popup, serviceDate, rows, loading = false, error = "", accessToken = "") {
  const bodyRows = loading
    ? `<tr><td colspan="5" class="empty">조회 중입니다.</td></tr>`
    : error
    ? `<tr><td colspan="5" class="empty">조회 실패: ${escapeHtml(error)}</td></tr>`
    : rows.length === 0
    ? `<tr><td colspan="5" class="empty">조회할 차량이 없습니다.</td></tr>`
    : rows.map((row, index) => `
        <tr
          class="data-row"
          data-profile-id="${escapeHtml(row.profileId || "")}"
          data-memo-encoded="${encodeURIComponent(row.memo || "")}"
        >
          <td>${escapeHtml(row.car_number)}</td>
          <td class="center">${escapeHtml(row.countLabel ?? row.count)}</td>
          <td>${escapeHtml(row.driver ? `${row.driver} (${row.monthlyWorkDays || 0})` : "")}</td>
          <td>${escapeHtml(row.phone)}</td>
          <td class="center">
            <button
              type="button"
              class="memo-btn"
              data-profile-id="${escapeHtml(row.profileId || "")}"
              data-driver="${escapeHtml(row.driver || row.car_number || "기사")}"
            >
              메모
            </button>
          </td>
        </tr>
      `).join("");

  popup.document.open();
  popup.document.write(`
    <!doctype html>
    <html lang="ko">
      <head>
        <meta charset="utf-8" />
        <title>미배차현황</title>
        <style>
          body { font-family: Arial, sans-serif; margin: 20px; color: #111827; }
          h1 { font-size: 20px; margin: 0 0 8px; }
          .date { margin-bottom: 16px; color: #4b5563; font-size: 14px; }
          .topbar { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 16px; }
          .date { margin-bottom: 0; color: #4b5563; font-size: 14px; }
          .memo-search { display: flex; align-items: center; gap: 6px; margin-left: auto; }
          .memo-search label { font-size: 14px; font-weight: 700; color: #374151; white-space: nowrap; }
          .memo-search input { width: 180px; border: 1px solid #d1d5db; border-radius: 4px; padding: 6px 8px; font-size: 14px; }
          .memo-search button { border: 1px solid #2563eb; background: #2563eb; color: #fff; border-radius: 4px; padding: 6px 10px; cursor: pointer; white-space: nowrap; }
          .memo-search button:hover { background: #1d4ed8; }
          table { width: 100%; border-collapse: collapse; font-size: 14px; }
          th { background: #3b82f6; color: #fff; position: sticky; top: 0; }
          th, td { border: 1px solid #d1d5db; padding: 8px; }
          .center { text-align: center; }
          .empty { text-align: center; color: #6b7280; padding: 24px; }
          .memo-btn { border: 1px solid #2563eb; background: #2563eb; color: #fff; border-radius: 4px; padding: 5px 10px; cursor: pointer; }
          .memo-btn:hover { background: #1d4ed8; }
          .modal-backdrop { display: none; position: fixed; inset: 0; background: rgba(0,0,0,.35); align-items: center; justify-content: center; padding: 16px; }
          .modal { width: 100%; max-width: 440px; background: #fff; border-radius: 6px; padding: 18px; box-shadow: 0 10px 30px rgba(0,0,0,.22); }
          .modal-title { font-weight: 700; margin-bottom: 10px; }
          textarea { width: 100%; min-height: 130px; box-sizing: border-box; border: 1px solid #d1d5db; border-radius: 4px; padding: 8px; font-family: inherit; font-size: 14px; resize: vertical; }
          .modal-actions { display: flex; justify-content: flex-end; gap: 8px; margin-top: 12px; }
          .modal-actions button { border: 1px solid #d1d5db; border-radius: 4px; background: #fff; padding: 7px 12px; cursor: pointer; }
          .modal-actions .save { border-color: #2563eb; background: #2563eb; color: #fff; }
        </style>
      </head>
      <body>
        <h1>미배차현황(운행횟수순)</h1>
        <div class="topbar">
          <div class="date">기준일: ${escapeHtml(serviceDate)}</div>
          <div class="memo-search">
            <label for="memoSearchText">메모 검색</label>
            <input id="memoSearchText" type="text" />
            <button type="button" id="memoSearchButton">메모 검색</button>
          </div>
        </div>
        <table>
          <thead>
            <tr>
              <th>차량번호</th>
              <th>운행횟수</th>
              <th>기사명</th>
              <th>연락처</th>
              <th>메모</th>
            </tr>
          </thead>
          <tbody>
            ${bodyRows}
            <tr id="memoSearchEmpty" style="display:none;"><td colspan="5" class="empty">해당 메모를 가진 기사가 없습니다.</td></tr>
          </tbody>
        </table>
        <div id="memoBackdrop" class="modal-backdrop">
          <div class="modal">
            <div id="memoTitle" class="modal-title">메모</div>
            <textarea id="memoText" placeholder="예) 1번 노선, 2번 노선, 3번 노선 가능"></textarea>
            <div class="modal-actions">
              <button type="button" id="memoCancel">취소</button>
              <button type="button" id="memoSave" class="save">저장</button>
            </div>
          </div>
        </div>
        <script>
          (function () {
            var activeKey = "";
            var activeRow = null;
            var activeProfileId = "";
            var backdrop = document.getElementById("memoBackdrop");
            var title = document.getElementById("memoTitle");
            var text = document.getElementById("memoText");
            var cancel = document.getElementById("memoCancel");
            var save = document.getElementById("memoSave");
            var memoSearchText = document.getElementById("memoSearchText");
            var memoSearchButton = document.getElementById("memoSearchButton");
            var memoSearchEmpty = document.getElementById("memoSearchEmpty");
            var supabaseUrl = "${escapeJs(supabaseUrl)}";
            var supabaseKey = "${escapeJs(supabaseKey)}";
            var accessToken = "${escapeJs(accessToken)}";

            function normalizeSearchText(value) {
              return (value || "")
                .toString()
                .toLowerCase()
                .replace(/[()\\[\\]{}<>]/g, " ")
                .replace(/\\s+/g, " ")
                .trim();
            }

            function getMemoForRow(row) {
              try {
                return decodeURIComponent(row.getAttribute("data-memo-encoded") || "");
              } catch (error) {
                return row.getAttribute("data-memo-encoded") || "";
              }
            }

            function setMemoForRow(row, memo) {
              if (!row) return;
              row.setAttribute("data-memo-encoded", encodeURIComponent(memo || ""));
            }

            async function saveProfileMemo(profileId, memo) {
              if (!profileId) {
                throw new Error("profiles에서 해당 기사를 찾지 못해 메모를 저장할 수 없습니다.");
              }

              var response = await fetch(
                supabaseUrl + "/rest/v1/profiles?id=eq." + encodeURIComponent(profileId),
                {
                  method: "PATCH",
                  headers: {
                    apikey: supabaseKey,
                    Authorization: "Bearer " + (accessToken || supabaseKey),
                    "Content-Type": "application/json",
                    Prefer: "return=minimal",
                  },
                  body: JSON.stringify({ memo: memo }),
                }
              );

              if (!response.ok) {
                var message = await response.text();
                throw new Error(message || response.statusText || "메모 저장 실패");
              }
            }

            function memoMatchesKeyword(memo, keyword) {
              if (!keyword) return true;
              var rawMemo = (memo || "").toString().toLowerCase();
              if (rawMemo.indexOf(keyword) !== -1) return true;
              return normalizeSearchText(rawMemo).indexOf(normalizeSearchText(keyword)) !== -1;
            }

            function applyMemoSearch() {
              var keyword = (memoSearchText.value || "").trim().toLowerCase();
              var visibleCount = 0;

              document.querySelectorAll("tr.data-row").forEach(function (row) {
                var memo = getMemoForRow(row);
                var visible = memoMatchesKeyword(memo, keyword);
                row.style.display = visible ? "" : "none";
                if (visible) visibleCount += 1;
              });

              if (memoSearchEmpty) {
                memoSearchEmpty.style.display = keyword && visibleCount === 0 ? "" : "none";
              }
            }

            function closeMemo() {
              backdrop.style.display = "none";
              activeKey = "";
            }

            document.querySelectorAll(".memo-btn").forEach(function (button) {
              button.addEventListener("click", function () {
                activeRow = button.closest("tr.data-row");
                activeProfileId = button.getAttribute("data-profile-id") || "";
                activeKey = activeProfileId;
                title.textContent = (button.getAttribute("data-driver") || "기사") + " 메모";
                text.value = activeRow ? getMemoForRow(activeRow) : "";
                backdrop.style.display = "flex";
                text.focus();
              });
            });

            cancel.addEventListener("click", closeMemo);
            backdrop.addEventListener("click", function (event) {
              if (event.target === backdrop) closeMemo();
            });
            save.addEventListener("click", async function () {
              save.disabled = true;
              try {
                await saveProfileMemo(activeProfileId, text.value);
                setMemoForRow(activeRow, text.value);
                closeMemo();
                applyMemoSearch();
              } catch (error) {
                alert("메모 저장 실패: 메모를 저장하지 못했습니다. 잠시 후 다시 시도해 주세요.");
              } finally {
                save.disabled = false;
              }
            });
            memoSearchButton.addEventListener("click", applyMemoSearch);
            memoSearchText.addEventListener("keydown", function (event) {
              if (event.key === "Enter") applyMemoSearch();
            });
            document.addEventListener("keydown", function (event) {
              if (event.key === "Escape") closeMemo();
            });
          })();
        </script>
      </body>
    </html>
  `);
  popup.document.close();
}

function escapeHtml(value = "") {
  return String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function escapeJs(value = "") {
  return String(value)
    .replace(/\\/g, "\\\\")
    .replace(/`/g, "\\`")
    .replace(/\$/g, "\\$")
    .replace(/<\/script>/gi, "<\\/script>");
}

function normalizePhone(value = "") {
  return String(value || "").replace(/\D/g, "");
}

function normalizeDriverName(value = "") {
  return String(value || "").trim().replace(/\s+/g, " ");
}
